<template>
  <div class="admin-designs-view container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Designs-Verwaltung</li>
      </ol>
    </nav>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <div>
          <h2 class="mb-0">
            <font-awesome-icon icon="file-alt" class="me-2" />
            Designs-Verwaltung
          </h2>
          <p class="text-muted mb-0">Designs erstellen, bearbeiten und verwalten</p>
          <div v-if="!hasNonProDefaultTemplate" class="alert alert-gradient-warning mt-2 mb-0">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            <strong>Hinweis:</strong> Kein Standard-Design für Free-Accounts gefunden.
            Standard-Designs müssen für alle Benutzer verfügbar sein (nicht Pro-Feature).
          </div>
        </div>
      </div>
      <BackButton fallback="admin" />
    </div>

    <!-- Action Cards -->
    <div class="row g-2 mb-3">
      <div class="col-md-6">
        <div class="card action-card h-100 shadow-sm border-0 hover-card">
          <div class="card-body text-center p-3">
            <div class="action-icon mb-2">
              <font-awesome-icon icon="tags" class="text-primary" size="lg" />
            </div>
            <div class="row" v-if="getFieldGroups(editingField.category).length > 0">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldGroup" class="form-label">Gruppe</label>
                  <select
                    id="editFieldGroup"
                    v-model="editingField.options.groupId"
                    class="form-select"
                  >
                    <option :value="null">Keine Gruppe</option>
                    <option
                      v-for="group in getFieldGroups(editingField.category)"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <div class="form-text">
                    Ändere die Standard-Gruppe für dieses Feld.
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-if="getFieldGroups(newField.category).length > 0">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldGroup" class="form-label">Gruppe</label>
                  <select
                    id="fieldGroup"
                    v-model="newField.options.groupId"
                    class="form-select"
                  >
                    <option :value="null">Keine Gruppe</option>
                    <option
                      v-for="group in getFieldGroups(newField.category)"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <div class="form-text">
                    Ordne dieses Feld einer Standard-Gruppe für das Template zu.
                  </div>
                </div>
              </div>
            </div>
            <h6 class="card-title mb-2">Kategorien verwalten</h6>
            <p class="card-text text-muted small mb-2">Design-Kategorien verwalten</p>
            <button
              @click="showCategoriesModal = true"
              class="btn btn-outline-primary btn-sm w-100"
            >
              <font-awesome-icon icon="tags" class="me-1" />
              Kategorien
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card action-card h-100 shadow-sm border-0 hover-card">
          <div class="card-body text-center p-3">
            <div class="action-icon mb-2">
              <font-awesome-icon icon="percentage" class="text-success" size="lg" />
            </div>
            <h6 class="card-title mb-2">Steuersätze verwalten</h6>
            <p class="card-text text-muted small mb-2">Steuersätze konfigurieren</p>
            <button @click="openTaxRatesModal" class="btn btn-outline-success btn-sm w-100">
              <font-awesome-icon icon="percentage" class="me-1" />
              Steuersätze
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Designs werden geladen...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Designs List -->
    <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
      <!-- Neues Design erstellen Card -->
      <div class="col">
        <div
          class="card h-100 border-dashed"
          @click="showCreateModal = true"
          style="cursor: pointer"
        >
          <div
            class="card-body d-flex flex-column justify-content-center align-items-center text-center py-4"
          >
            <div class="mb-3">
              <font-awesome-icon icon="plus-circle" size="3x" class="text-primary" />
            </div>
            <h5 class="card-title text-primary mb-2">Neues Design erstellen</h5>
            <p class="card-text text-muted mb-0">
              Neues Design-Template erstellen und konfigurieren
            </p>
          </div>
        </div>
      </div>

      <!-- Design Cards -->
      <div v-for="template in sortedTemplates" :key="template.id" class="col">
        <div
          class="card h-100 admin-template-card"
          :class="{ 'border-warning': template.isDefault }"
        >
          <div class="card-body">
            <div class="admin-template-header">
              <div class="admin-template-title-section">
                <h5 class="admin-template-title" :title="template.displayName">
                  {{ template.displayName }}
                </h5>
                <div class="admin-template-badges">
                  <span
                    v-if="template.isDefault"
                    class="badge bg-warning text-dark"
                    title="Standard-Design"
                  >
                    <font-awesome-icon icon="star" class="me-1" />
                    Standard
                  </span>
                  <span
                    v-else-if="template.isProFeature"
                    class="badge bg-primary"
                    title="Pro-Feature"
                  >
                    <font-awesome-icon icon="crown" class="me-1" />
                    Pro
                  </span>
                </div>
              </div>
              <div class="admin-template-actions">
                <button
                  class="btn btn-sm btn-outline-secondary admin-template-dropdown"
                  type="button"
                  :id="`dropdown-${template.id}`"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  @click.stop
                >
                  <font-awesome-icon icon="ellipsis-v" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="editTemplate(template)">
                      <font-awesome-icon icon="edit" class="me-2" />
                      Bearbeiten
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="manageFields(template)">
                      <font-awesome-icon icon="list" class="me-2" />
                      Felder verwalten
                    </a>
                  </li>
                  <li>
                    <a
                      class="dropdown-item"
                      href="#"
                      @click.prevent="setAsDefault(template)"
                      :class="{ disabled: template.isDefault || template.isProFeature }"
                      v-if="!template.isProFeature"
                    >
                      <font-awesome-icon icon="star" class="me-2" />
                      Als Standard setzen
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a
                      class="dropdown-item text-danger"
                      href="#"
                      @click.prevent="deleteTemplate(template)"
                      :class="{ disabled: template.isDefault }"
                    >
                      <font-awesome-icon icon="trash" class="me-2" />
                      Löschen
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="admin-template-meta">
              <small class="text-muted">
                <font-awesome-icon icon="tag" class="me-1" />
                {{ template.name }}
              </small>
            </div>

            <p class="admin-template-description" :title="template.description">
              {{ template.description || 'Keine Beschreibung' }}
            </p>

            <div class="admin-template-stats">
              <div class="admin-template-stat">
                <font-awesome-icon icon="list-alt" class="text-info me-1" />
                <span class="fw-bold text-info"> {{ template.fieldCount || 0 }} Felder </span>
              </div>
              <div class="admin-template-stat">
                <font-awesome-icon
                  :icon="template.isActive ? 'check-circle' : 'times-circle'"
                  :class="template.isActive ? 'text-success' : 'text-danger'"
                  class="me-1"
                />
                <span class="small" :class="template.isActive ? 'text-success' : 'text-danger'">
                  {{ template.isActive ? 'Aktiv' : 'Inaktiv' }}
                </span>
              </div>
            </div>

            <div class="admin-template-category">
              <span class="badge bg-secondary">
                <font-awesome-icon icon="folder" class="me-1" />
                {{ getCategoryLabel(template) }}
              </span>
            </div>

            <!-- Signature Requirement Toggle -->
            <div class="admin-template-signature-toggle mt-3">
              <div class="form-check form-switch">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :id="`needsSignatureToggle-${template.id}`"
                  v-model="template.needsSignature"
                  @change="updateTemplateNeedsSignature(template)"
                  :disabled="updatingSignature[template.id]"
                >
                <label class="form-check-label" :for="`needsSignatureToggle-${template.id}`">
                  <font-awesome-icon icon="signature" class="me-1" />
                  Signatur erforderlich
                </label>
              </div>
              <div class="form-text small">
                <font-awesome-icon icon="info-circle" class="me-1" />
                Bestimmt ob Quittungen mit diesem Design eine Signatur benötigen.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Design Modal -->
    <div
      class="modal"
      id="createTemplateModal"
      tabindex="-1"
      :class="{ show: showCreateModal }"
      :style="{ display: showCreateModal ? 'block' : 'none' }"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="plus" class="me-2" />
              Neues Design erstellen
            </h5>
            <button type="button" class="btn-close" @click="showCreateModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="createTemplate">
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="templateName" class="form-label">Design-Name</label>
                  <input
                    v-model="newTemplate.name"
                    type="text"
                    class="form-control"
                    id="templateName"
                    placeholder="z.B. volleyball"
                    required
                  />
                  <div class="form-text">
                    Eindeutiger technischer Name (lowercase, ohne Leerzeichen)
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="templateDisplayName" class="form-label">Anzeigename</label>
                  <input
                    v-model="newTemplate.displayName"
                    type="text"
                    class="form-control"
                    id="templateDisplayName"
                    placeholder="z.B. Volleyball"
                    required
                  />
                </div>
                <div class="col-12">
                  <label for="templateDescription" class="form-label">Beschreibung</label>
                  <textarea
                    v-model="newTemplate.description"
                    class="form-control"
                    id="templateDescription"
                    rows="3"
                    placeholder="Beschreibung des Designs..."
                  ></textarea>
                </div>
                <div class="col-md-6">
                  <label for="templateCategory" class="form-label"
                    >Kategorie <small class="text-muted">(optional)</small></label
                  >
                  <select
                    v-model="newTemplate.categoryId"
                    class="form-select"
                    id="templateCategory"
                  >
                    <option value="">-- Keine Kategorie --</option>
                    <option v-for="category in categories" :key="category.id" :value="category.id">
                      {{ category.displayName }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6">
                  <div class="mt-4">
                    <div class="form-check">
                      <input
                        v-model="newTemplate.isProFeature"
                        type="checkbox"
                        class="form-check-input"
                        id="templateProFeature"
                      />
                      <label class="form-check-label" for="templateProFeature"> Pro-Feature </label>
                    </div>
                    <div class="form-check">
                      <input
                        v-model="newTemplate.allowCustomFields"
                        type="checkbox"
                        class="form-check-input"
                        id="templateAllowCustom"
                      />
                      <label class="form-check-label" for="templateAllowCustom">
                        Custom Felder erlauben
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Bereichsnamen-Einstellungen -->
                <div class="col-12">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="mb-0">
                        <font-awesome-icon icon="tags" class="me-2" />
                        Bereichsnamen anpassen (optional)
                      </h6>
                      <small class="text-muted"
                        >Definiere spezifische Namen für die Quittungsbereiche</small
                      >
                    </div>
                    <div class="card-body">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label for="newPersonalCategoryName" class="form-label"
                            >Persönliche Daten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.personalCategoryIcon || 'user'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.personalCategoryName"
                              type="text"
                              class="form-control"
                              id="newPersonalCategoryName"
                              placeholder="z.B. Schiedsrichter-Daten"
                            />
                          </div>
                          <div class="form-text">Name für den persönlichen Bereich</div>
                        </div>
                        <div class="col-md-6">
                          <label for="newPersonalCategoryIcon" class="form-label"
                            >Icon für persönliche Daten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.personalCategoryIcon || 'user'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.personalCategoryIcon"
                              type="text"
                              class="form-control"
                              id="newPersonalCategoryIcon"
                              placeholder="z.B. user, user-tie, user-graduate"
                            />
                          </div>
                          <div class="form-text">
                            Font Awesome Icon-Klasse (z.B. user, user-tie, user-graduate)
                          </div>
                        </div>
                        <div class="col-md-6">
                          <label for="newReceiptCategoryName" class="form-label"
                            >Quittungsdaten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.receiptCategoryIcon || 'receipt'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.receiptCategoryName"
                              type="text"
                              class="form-control"
                              id="newReceiptCategoryName"
                              placeholder="z.B. Spielinformationen"
                            />
                          </div>
                          <div class="form-text">Name für den Quittungsbereich</div>
                        </div>
                        <div class="col-md-6">
                          <label for="newReceiptCategoryIcon" class="form-label"
                            >Icon für Quittungsdaten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.receiptCategoryIcon || 'receipt'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.receiptCategoryIcon"
                              type="text"
                              class="form-control"
                              id="newReceiptCategoryIcon"
                              placeholder="z.B. receipt, file-invoice, clipboard-list"
                            />
                          </div>
                          <div class="form-text">
                            Font Awesome Icon-Klasse (z.B. receipt, file-invoice, clipboard-list)
                          </div>
                        </div>
                        <div class="col-md-6">
                          <label for="newFinancialCategoryName" class="form-label"
                            >Finanzielle Daten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.financialCategoryIcon || 'money-bill'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.financialCategoryName"
                              type="text"
                              class="form-control"
                              id="newFinancialCategoryName"
                              placeholder="z.B. Vergütung"
                            />
                          </div>
                          <div class="form-text">Name für den finanziellen Bereich</div>
                        </div>
                        <div class="col-md-6">
                          <label for="newFinancialCategoryIcon" class="form-label"
                            >Icon für finanzielle Daten</label
                          >
                          <div class="input-group">
                            <span class="input-group-text">
                              <font-awesome-icon
                                :icon="newTemplate.financialCategoryIcon || 'money-bill'"
                              />
                            </span>
                            <input
                              v-model="newTemplate.financialCategoryIcon"
                              type="text"
                              class="form-control"
                              id="newFinancialCategoryIcon"
                              placeholder="z.B. money-bill, credit-card, euro-sign"
                            />
                          </div>
                          <div class="form-text">
                            Font Awesome Icon-Klasse (z.B. money-bill, credit-card, euro-sign)
                          </div>
                        </div>
                      </div>
                      <div class="mt-3">
                        <small class="text-muted">
                          <font-awesome-icon icon="info-circle" class="me-1" />
                          Leer lassen für Standard-Namen und Icons. Diese werden im Receipt Wizard
                          angezeigt.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCreateModal = false">
              Abbrechen
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="createTemplate"
              :disabled="creating"
            >
              <span v-if="creating" class="spinner-border spinner-border-sm me-2"></span>
              Design erstellen
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showCreateModal"
      class="modal-backdrop fade show"
      @click="showCreateModal = false"
    ></div>

    <!-- Categories Management Modal -->
    <div
      class="modal"
      id="categoriesModal"
      tabindex="-1"
      :class="{ show: showCategoriesModal }"
      :style="{ display: showCategoriesModal ? 'block' : 'none' }"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="tags" class="me-2" />
              Kategorien verwalten
            </h5>
            <button type="button" class="btn-close" @click="showCategoriesModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6>Vorhandene Kategorien</h6>
              <button
                @click="showNewCategoryForm = !showNewCategoryForm"
                class="btn btn-sm btn-primary"
              >
                <font-awesome-icon icon="plus" class="me-1" />
                Neue Kategorie
              </button>
            </div>

            <!-- New Category Form -->
            <div v-if="showNewCategoryForm" class="card mb-3">
              <div class="card-body">
                <form @submit.prevent="createCategory">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Name</label>
                      <input
                        v-model="newCategory.name"
                        type="text"
                        class="form-control"
                        placeholder="z.B. volleyball"
                        required
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Anzeigename</label>
                      <input
                        v-model="newCategory.displayName"
                        type="text"
                        class="form-control"
                        placeholder="z.B. Volleyball"
                        required
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Farbe</label>
                      <input
                        v-model="newCategory.color"
                        type="color"
                        class="form-control form-control-color"
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Icon</label>
                      <input
                        v-model="newCategory.icon"
                        type="text"
                        class="form-control"
                        placeholder="z.B. volleyball-ball"
                      />
                    </div>
                    <div class="col-12">
                      <label class="form-label">Beschreibung</label>
                      <textarea
                        v-model="newCategory.description"
                        class="form-control"
                        rows="2"
                        placeholder="Beschreibung der Kategorie..."
                      ></textarea>
                    </div>
                    <div class="col-12">
                      <button type="submit" class="btn btn-primary btn-sm me-2">Erstellen</button>
                      <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        @click="resetNewCategory"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <!-- Categories List -->
            <div class="row g-3">
              <div v-for="category in categories" :key="category.id" class="col-md-6">
                <div class="card category-card h-100">
                  <div class="card-body d-flex flex-column">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <div class="d-flex align-items-center">
                        <div
                          class="category-color-indicator me-2"
                          :style="{ backgroundColor: category.color }"
                        ></div>
                        <div>
                          <h6 class="mb-0">{{ category.displayName }}</h6>
                          <small class="text-muted">{{ category.name }}</small>
                        </div>
                      </div>
                      <div class="dropdown">
                        <button
                          class="btn btn-sm btn-outline-secondary dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                        >
                          <font-awesome-icon icon="ellipsis-v" />
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                          <li>
                            <a
                              class="dropdown-item text-danger"
                              href="#"
                              @click.prevent="deleteCategory(category)"
                            >
                              <font-awesome-icon icon="trash" class="me-2" />
                              Löschen
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <p class="text-muted small flex-grow-1">
                      {{ category.description || 'Keine Beschreibung' }}
                    </p>
                    <div class="d-flex justify-content-between align-items-center mt-auto">
                      <small class="text-muted">
                        {{ category.templates?.length || 0 }} Designs
                      </small>
                      <div>
                        <span v-if="!category.isActive" class="badge bg-secondary"> Inaktiv </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCategoriesModal = false">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showCategoriesModal"
      class="modal-backdrop fade show"
      @click="showCategoriesModal = false"
    ></div>
  </div>

  <!-- Edit Design Modal -->
  <div
    class="modal"
    id="editModal"
    tabindex="-1"
    :class="{ show: showEditModal }"
    :style="{ display: showEditModal ? 'block' : 'none' }"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="edit" class="me-2" />
            Design bearbeiten
          </h5>
          <button type="button" class="btn-close" @click="showEditModal = false"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateTemplate">
            <div class="row g-3">
              <div class="col-md-6">
                <label for="editTemplateName" class="form-label">Name</label>
                <input
                  v-model="editingTemplate.name"
                  type="text"
                  class="form-control"
                  id="editTemplateName"
                  placeholder="z.B. volleyball"
                  required
                  :disabled="editingTemplate.isDefault"
                />
                <div class="form-text">
                  <span v-if="!editingTemplate.isDefault"
                    >Eindeutiger technischer Name (lowercase, ohne Leerzeichen)</span
                  >
                  <span v-else class="text-muted"
                    >Standard-Design-Namen können nicht geändert werden</span
                  >
                </div>
              </div>
              <div class="col-md-6">
                <label for="editTemplateDisplayName" class="form-label">Anzeigename</label>
                <input
                  v-model="editingTemplate.displayName"
                  type="text"
                  class="form-control"
                  id="editTemplateDisplayName"
                  placeholder="z.B. Volleyball"
                  required
                />
              </div>
              <div class="col-12">
                <label for="editTemplateDescription" class="form-label">Beschreibung</label>
                <textarea
                  v-model="editingTemplate.description"
                  class="form-control"
                  id="editTemplateDescription"
                  rows="3"
                  placeholder="Beschreibung des Templates..."
                ></textarea>
              </div>

              <!-- Bereichsnamen-Einstellungen -->
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <font-awesome-icon icon="tags" class="me-2" />
                      Bereichsnamen anpassen
                    </h6>
                    <small class="text-muted"
                      >Definiere spezifische Namen für die Quittungsbereiche</small
                    >
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label for="editPersonalCategoryName" class="form-label"
                          >Persönliche Daten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.personalCategoryIcon || 'user'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.personalCategoryName"
                            type="text"
                            class="form-control"
                            id="editPersonalCategoryName"
                            placeholder="z.B. Schiedsrichter-Daten"
                          />
                        </div>
                        <div class="form-text">Name für den persönlichen Bereich</div>
                      </div>
                      <div class="col-md-6">
                        <label for="editPersonalCategoryIcon" class="form-label"
                          >Icon für persönliche Daten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.personalCategoryIcon || 'user'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.personalCategoryIcon"
                            type="text"
                            class="form-control"
                            id="editPersonalCategoryIcon"
                            placeholder="z.B. user, user-tie, user-graduate"
                          />
                        </div>
                        <div class="form-text">
                          Font Awesome Icon-Klasse (z.B. user, user-tie, user-graduate)
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="editReceiptCategoryName" class="form-label"
                          >Quittungsdaten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.receiptCategoryIcon || 'receipt'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.receiptCategoryName"
                            type="text"
                            class="form-control"
                            id="editReceiptCategoryName"
                            placeholder="z.B. Spielinformationen"
                          />
                        </div>
                        <div class="form-text">Name für den Quittungsbereich</div>
                      </div>
                      <div class="col-md-6">
                        <label for="editReceiptCategoryIcon" class="form-label"
                          >Icon für Quittungsdaten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.receiptCategoryIcon || 'receipt'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.receiptCategoryIcon"
                            type="text"
                            class="form-control"
                            id="editReceiptCategoryIcon"
                            placeholder="z.B. receipt, file-invoice, clipboard-list"
                          />
                        </div>
                        <div class="form-text">
                          Font Awesome Icon-Klasse (z.B. receipt, file-invoice, clipboard-list)
                        </div>
                      </div>
                      <div class="col-md-6">
                        <label for="editFinancialCategoryName" class="form-label"
                          >Finanzielle Daten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.financialCategoryIcon || 'money-bill'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.financialCategoryName"
                            type="text"
                            class="form-control"
                            id="editFinancialCategoryName"
                            placeholder="z.B. Vergütung"
                          />
                        </div>
                        <div class="form-text">Name für den finanziellen Bereich</div>
                      </div>
                      <div class="col-md-6">
                        <label for="editFinancialCategoryIcon" class="form-label"
                          >Icon für finanzielle Daten</label
                        >
                        <div class="input-group">
                          <span class="input-group-text">
                            <font-awesome-icon
                              :icon="editingTemplate.financialCategoryIcon || 'money-bill'"
                            />
                          </span>
                          <input
                            v-model="editingTemplate.financialCategoryIcon"
                            type="text"
                            class="form-control"
                            id="editFinancialCategoryIcon"
                            placeholder="z.B. money-bill, credit-card, euro-sign"
                          />
                        </div>
                        <div class="form-text">
                          Font Awesome Icon-Klasse (z.B. money-bill, credit-card, euro-sign)
                        </div>
                      </div>
                    </div>
                    <div class="mt-3">
                      <small class="text-muted">
                        <font-awesome-icon icon="info-circle" class="me-1" />
                        Leer lassen für Standard-Namen und Icons. Diese werden im Receipt Wizard
                        angezeigt.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label for="editTemplateCategory" class="form-label"
                  >Kategorie <small class="text-muted">(optional)</small></label
                >
                <select
                  v-model="editingTemplate.categoryId"
                  class="form-select"
                  id="editTemplateCategory"
                >
                  <option value="">-- Keine Kategorie --</option>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                    {{ category.displayName }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <div class="d-flex flex-column gap-2">
                  <div class="form-check">
                    <input
                      v-model="editingTemplate.isProFeature"
                      class="form-check-input"
                      type="checkbox"
                      id="editIsProFeature"
                      :disabled="editingTemplate.isDefault"
                    />
                    <label class="form-check-label" for="editIsProFeature">
                      Pro-Feature
                      <small v-if="editingTemplate.isDefault" class="text-muted ms-1"
                        >(Standard-Templates können nicht Pro-Feature sein)</small
                      >
                    </label>
                  </div>
                  <div class="form-check">
                    <input
                      v-model="editingTemplate.allowCustomFields"
                      class="form-check-input"
                      type="checkbox"
                      id="editAllowCustomFields"
                    />
                    <label class="form-check-label" for="editAllowCustomFields">
                      Benutzerdefinierte Felder erlauben
                    </label>
                  </div>
                  <div class="form-check">
                    <input
                      v-model="editingTemplate.isActive"
                      class="form-check-input"
                      type="checkbox"
                      id="editIsActive"
                      :disabled="editingTemplate.isDefault"
                    />
                    <label class="form-check-label" for="editIsActive">
                      Aktiv
                      <small v-if="editingTemplate.isDefault" class="text-muted ms-1"
                        >(Standard-Templates sind immer aktiv)</small
                      >
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showEditModal = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="updateTemplate"
            :disabled="updating"
          >
            <span v-if="updating" class="spinner-border spinner-border-sm me-2"></span>
            {{ updating ? 'Wird aktualisiert...' : 'Aktualisieren' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showEditModal" class="modal-backdrop fade show" @click="showEditModal = false"></div>

  <!-- Fields Management Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showFieldsModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="fieldsModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="fieldsModalLabel">
            <font-awesome-icon icon="shield-alt" class="me-2" />
            Felder verwalten: {{ selectedTemplateForFields?.displayName }}
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showFieldsModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Fields List -->
          <div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">
                Admin-Felder ({{ selectedTemplateForFields.adminFields.length }})
              </h6>
              <button
                @click="openCreateFieldModal"
                class="btn btn-primary btn-sm"
                title="Neues Admin-Feld erstellen"
              >
                <font-awesome-icon icon="plus" class="me-1" />
                Neues Feld
              </button>
            </div>

            <div v-if="selectedTemplateForFields.adminFields.length === 0" class="text-center py-4">
              <font-awesome-icon
                icon="shield-alt"
                class="text-muted mb-3"
                style="font-size: 3rem"
              />
              <p class="text-muted">Keine Admin-Felder definiert</p>
              <button
                @click="openCreateFieldModal"
                class="btn btn-primary"
                title="Neues Admin-Feld erstellen"
              >
                <font-awesome-icon icon="plus" class="me-1" />
                Erstes Feld hinzufügen
              </button>
              <div class="mt-2">
                <small class="text-muted">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Admin-Felder werden von Administratoren erstellt und können nur von
                  Administratoren bearbeitet werden.
                </small>
              </div>
            </div>

            <div v-else>
              <!-- Category Tabs -->
              <ul class="nav nav-tabs mb-3" id="fieldCategoryTabs" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'receipt' }"
                    @click="activeTab = 'receipt'"
                    type="button"
                    role="tab"
                  >
                    <font-awesome-icon icon="receipt" class="me-1" />
                    Quittung ({{ getFieldsByCategory('receipt').length }})
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'financial' }"
                    @click="activeTab = 'financial'"
                    type="button"
                    role="tab"
                  >
                    <font-awesome-icon icon="money-bill" class="me-1" />
                    Finanziell ({{ getFieldsByCategory('financial').length }})
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ active: activeTab === 'personal' }"
                    @click="activeTab = 'personal'"
                    type="button"
                    role="tab"
                  >
                    <font-awesome-icon icon="user" class="me-1" />
                    Persönlich ({{ getFieldsByCategory('personal').length }})
                  </button>
                </li>
              </ul>

              <!-- Tab Content -->
              <div id="fieldCategoryTabContent">
                <!-- Personal Fields -->
                <div v-show="activeTab === 'personal'" class="tab-pane" role="tabpanel">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Persönliche Felder</h6>
                    <div class="btn-group btn-group-sm">
                      <button
                        @click="toggleOrderMode('personal')"
                        class="btn btn-outline-secondary"
                        :class="{ active: orderMode === 'personal' }"
                        title="Reihenfolge ändern"
                      >
                        <font-awesome-icon icon="arrows-alt" class="me-1" />
                        {{ orderMode === 'personal' ? 'Beenden' : 'Reihenfolge' }}
                      </button>
                      <button
                        v-if="orderMode === 'personal'"
                        @click="saveFieldOrder('personal')"
                        class="btn btn-success"
                        :disabled="savingOrder"
                      >
                        <font-awesome-icon icon="save" class="me-1" />
                        {{ savingOrder ? 'Speichern...' : 'Speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="card mb-3">
                    <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div>
                        <h6 class="mb-0">Gruppen für Persönliche Felder</h6>
                        <small class="text-muted">Gruppiere Felder zu farbigen Bereichen im Wizard.</small>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="addFieldGroup('personal')"
                        >
                          <font-awesome-icon icon="layer-group" class="me-1" />
                          Gruppe hinzufügen
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <div v-if="getFieldGroups('personal').length === 0" class="text-muted small">
                        Noch keine Gruppen vorhanden. Füge eine Gruppe hinzu, um Felder zu bündeln.
                      </div>
                      <div v-else class="field-group-list">
                        <div
                          v-for="(group, index) in getFieldGroups('personal')"
                          :key="group.id"
                          class="field-group-item border rounded p-3 mb-2"
                        >
                          <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">
                            <div class="d-flex align-items-center gap-2 flex-grow-1 w-100">
                              <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                              <input
                                type="text"
                                class="form-control"
                                v-model="group.name"
                                placeholder="Gruppenname"
                                @input="markFieldGroupsDirty()"
                              />
                            </div>
                            <div class="d-flex align-items-center gap-3 flex-wrap">
                              <div class="d-flex align-items-center gap-2">
                                <label class="form-label small mb-0 text-muted">Farbe</label>
                                <input
                                  type="color"
                                  class="form-control form-control-color"
                                  v-model="group.color"
                                  @input="markFieldGroupsDirty()"
                                  aria-label="Gruppenfarbe wählen"
                                />
                              </div>
                              <span class="badge bg-light text-dark">
                                {{ countFieldsInGroup('personal', group.id) }} Feld(er)
                              </span>
                              <div class="btn-group btn-group-sm" role="group" aria-label="Gruppenreihenfolge">
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('personal', index, -1)"
                                  :disabled="index === 0"
                                  title="Nach oben"
                                >
                                  <font-awesome-icon icon="arrow-up" />
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('personal', index, 1)"
                                  :disabled="index === getFieldGroups('personal').length - 1"
                                  title="Nach unten"
                                >
                                  <font-awesome-icon icon="arrow-down" />
                                </button>
                              </div>
                              <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                @click="removeFieldGroup('personal', group)"
                                title="Gruppe löschen"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                      <small class="text-muted">
                        Änderungen an Gruppen müssen gespeichert werden, damit sie im Wizard erscheinen.
                      </small>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="saveTemplateFieldGroups"
                        :disabled="!fieldGroupsDirty || savingFieldGroups"
                      >
                        <span v-if="savingFieldGroups" class="spinner-border spinner-border-sm me-2"></span>
                        {{ savingFieldGroups ? 'Speichern...' : 'Gruppen speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th v-if="orderMode === 'personal'" style="width: 50px">#</th>
                          <th>Name</th>
                          <th>Typ</th>
                          <th>Erforderlich</th>
                          <th>Gruppe</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(field, index) in getFieldsByCategory('personal')"
                          :key="field.id"
                          :class="{ 'table-warning': orderMode === 'personal' }"
                          :draggable="orderMode === 'personal'"
                          @dragstart="dragStart($event, field, 'personal')"
                          @dragover.prevent
                          @drop="drop($event, field, 'personal')"
                        >
                          <td v-if="orderMode === 'personal'" class="text-center">
                            <font-awesome-icon icon="grip-vertical" class="text-muted" />
                          </td>
                          <td>
                            <strong>{{ field.displayName }}</strong>
                            <br />
                            <small class="text-muted">{{ field.name }}</small>
                            <div class="mt-1">
                              <span v-if="field.isAdminField" class="badge bg-primary me-2">
                                <font-awesome-icon icon="shield-alt" class="me-1" />
                                Admin
                              </span>
                              <span v-if="field.isCalculated" class="badge bg-info me-2">
                                <font-awesome-icon icon="calculator" class="me-1" />
                                Berechnet
                              </span>
                              <span v-if="field.undeletable" class="badge bg-warning text-dark">
                                <font-awesome-icon icon="lock" class="me-1" />
                                Fixed
                              </span>
                            </div>
                          </td>
                          <td>
                            <span class="badge bg-info">{{ getFieldTypeLabel(field.type) }}</span>
                          </td>
                          <td>
                            <span v-if="field.required" class="badge bg-danger">Erforderlich</span>
                            <span v-else class="text-muted">Optional</span>
                          </td>
                          <td>
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                              <span
                                class="group-color-dot"
                                v-if="field.options?.groupId"
                                :style="{ backgroundColor: getGroupColor('personal', field.options.groupId) }"
                                :title="getGroupName('personal', field.options.groupId)"
                              ></span>
                              <select
                                class="form-select form-select-sm"
                                :value="field.options?.groupId || ''"
                                :disabled="orderMode === 'personal' || updatingFieldGroupId === field.id"
                                @change="onFieldGroupAssignmentChange(field, 'personal', $event.target.value)"
                              >
                                <option value="">Keine Gruppe</option>
                                <option
                                  v-for="group in getFieldGroups('personal')"
                                  :key="group.id"
                                  :value="group.id"
                                >
                                  {{ group.name }}
                                </option>
                              </select>
                              <span
                                v-if="updatingFieldGroupId === field.id"
                                class="spinner-border spinner-border-sm text-muted"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </div>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm">
                              <button
                                @click="editField(field)"
                                class="btn btn-outline-primary"
                                title="Feld bearbeiten"
                                :disabled="orderMode === 'personal'"
                              >
                                <font-awesome-icon icon="edit" />
                              </button>
                              <button
                                @click="deleteField(field)"
                                class="btn btn-outline-danger"
                                :title="
                                  field.undeletable
                                    ? 'Feld löschen (Admin-Berechtigung erforderlich)'
                                    : 'Feld löschen'
                                "
                                :disabled="orderMode === 'personal'"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Receipt Fields -->
                <div v-if="activeTab === 'receipt'" class="tab-pane" role="tabpanel">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Quittungsfelder</h6>
                    <div class="btn-group btn-group-sm">
                      <button
                        @click="toggleOrderMode('receipt')"
                        class="btn btn-outline-secondary"
                        :class="{ active: orderMode === 'receipt' }"
                        title="Reihenfolge ändern"
                      >
                        <font-awesome-icon icon="arrows-alt" class="me-1" />
                        {{ orderMode === 'receipt' ? 'Beenden' : 'Reihenfolge' }}
                      </button>
                      <button
                        v-if="orderMode === 'receipt'"
                        @click="saveFieldOrder('receipt')"
                        class="btn btn-success"
                        :disabled="savingOrder"
                      >
                        <font-awesome-icon icon="save" class="me-1" />
                        {{ savingOrder ? 'Speichern...' : 'Speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="card mb-3">
                    <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div>
                        <h6 class="mb-0">Gruppen für Quittungsfelder</h6>
                        <small class="text-muted">Definiere Standard-Gruppen für Quittungsfelder.</small>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="addFieldGroup('receipt')"
                        >
                          <font-awesome-icon icon="layer-group" class="me-1" />
                          Gruppe hinzufügen
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <div v-if="getFieldGroups('receipt').length === 0" class="text-muted small">
                        Noch keine Gruppen vorhanden. Füge eine Gruppe hinzu, um Felder vorzustrukturieren.
                      </div>
                      <div v-else class="field-group-list">
                        <div
                          v-for="(group, index) in getFieldGroups('receipt')"
                          :key="group.id"
                          class="field-group-item border rounded p-3 mb-2"
                        >
                          <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">
                            <div class="d-flex align-items-center gap-2 flex-grow-1 w-100">
                              <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                              <input
                                type="text"
                                class="form-control"
                                v-model="group.name"
                                placeholder="Gruppenname"
                                @input="markFieldGroupsDirty()"
                              />
                            </div>
                            <div class="d-flex align-items-center gap-3 flex-wrap">
                              <div class="d-flex align-items-center gap-2">
                                <label class="form-label small mb-0 text-muted">Farbe</label>
                                <input
                                  type="color"
                                  class="form-control form-control-color"
                                  v-model="group.color"
                                  @input="markFieldGroupsDirty()"
                                  aria-label="Gruppenfarbe wählen"
                                />
                              </div>
                              <span class="badge bg-light text-dark">
                                {{ countFieldsInGroup('receipt', group.id) }} Feld(er)
                              </span>
                              <div class="btn-group btn-group-sm" role="group" aria-label="Gruppenreihenfolge">
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('receipt', index, -1)"
                                  :disabled="index === 0"
                                  title="Nach oben"
                                >
                                  <font-awesome-icon icon="arrow-up" />
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('receipt', index, 1)"
                                  :disabled="index === getFieldGroups('receipt').length - 1"
                                  title="Nach unten"
                                >
                                  <font-awesome-icon icon="arrow-down" />
                                </button>
                              </div>
                              <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                @click="removeFieldGroup('receipt', group)"
                                title="Gruppe löschen"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                      <small class="text-muted">
                        Änderungen an Gruppen müssen gespeichert werden, damit sie im Wizard erscheinen.
                      </small>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="saveTemplateFieldGroups"
                        :disabled="!fieldGroupsDirty || savingFieldGroups"
                      >
                        <span v-if="savingFieldGroups" class="spinner-border spinner-border-sm me-2"></span>
                        {{ savingFieldGroups ? 'Speichern...' : 'Gruppen speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th v-if="orderMode === 'receipt'" style="width: 50px">#</th>
                          <th>Name</th>
                          <th>Typ</th>
                          <th>Erforderlich</th>
                          <th>Gruppe</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(field, index) in getFieldsByCategory('receipt')"
                          :key="field.id"
                          :class="{ 'table-warning': orderMode === 'receipt' }"
                          :draggable="orderMode === 'receipt'"
                          @dragstart="dragStart($event, field, 'receipt')"
                          @dragover.prevent
                          @drop="drop($event, field, 'receipt')"
                        >
                          <td v-if="orderMode === 'receipt'" class="text-center">
                            <font-awesome-icon icon="grip-vertical" class="text-muted" />
                          </td>
                          <td>
                            <strong>{{ field.displayName }}</strong>
                            <br />
                            <small class="text-muted">{{ field.name }}</small>
                            <div class="mt-1">
                              <span v-if="field.isAdminField" class="badge bg-primary me-2">
                                <font-awesome-icon icon="shield-alt" class="me-1" />
                                Admin
                              </span>
                              <span v-if="field.isCalculated" class="badge bg-info me-2">
                                <font-awesome-icon icon="calculator" class="me-1" />
                                Berechnet
                              </span>
                              <span v-if="field.undeletable" class="badge bg-warning text-dark">
                                <font-awesome-icon icon="lock" class="me-1" />
                                Fixed
                              </span>
                            </div>
                          </td>
                          <td>
                            <span class="badge bg-info">{{ getFieldTypeLabel(field.type) }}</span>
                          </td>
                          <td>
                            <span v-if="field.required" class="badge bg-danger">Erforderlich</span>
                            <span v-else class="text-muted">Optional</span>
                          </td>
                          <td>
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                              <span
                                class="group-color-dot"
                                v-if="field.options?.groupId"
                                :style="{ backgroundColor: getGroupColor('receipt', field.options.groupId) }"
                                :title="getGroupName('receipt', field.options.groupId)"
                              ></span>
                              <select
                                class="form-select form-select-sm"
                                :value="field.options?.groupId || ''"
                                :disabled="orderMode === 'receipt' || updatingFieldGroupId === field.id"
                                @change="onFieldGroupAssignmentChange(field, 'receipt', $event.target.value)"
                              >
                                <option value="">Keine Gruppe</option>
                                <option
                                  v-for="group in getFieldGroups('receipt')"
                                  :key="group.id"
                                  :value="group.id"
                                >
                                  {{ group.name }}
                                </option>
                              </select>
                              <span
                                v-if="updatingFieldGroupId === field.id"
                                class="spinner-border spinner-border-sm text-muted"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </div>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm">
                              <button
                                @click="editField(field)"
                                class="btn btn-outline-primary"
                                title="Feld bearbeiten"
                                :disabled="orderMode === 'receipt'"
                              >
                                <font-awesome-icon icon="edit" />
                              </button>
                              <button
                                @click="deleteField(field)"
                                class="btn btn-outline-danger"
                                :title="
                                  field.undeletable
                                    ? 'Feld löschen (Admin-Berechtigung erforderlich)'
                                    : 'Feld löschen'
                                "
                                :disabled="orderMode === 'receipt'"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Financial Fields -->
                <div v-show="activeTab === 'financial'" class="tab-pane" role="tabpanel">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Finanzfelder</h6>
                    <div class="btn-group btn-group-sm">
                      <button
                        @click="toggleOrderMode('financial')"
                        class="btn btn-outline-secondary"
                        :class="{ active: orderMode === 'financial' }"
                        title="Reihenfolge ändern"
                      >
                        <font-awesome-icon icon="arrows-alt" class="me-1" />
                        {{ orderMode === 'financial' ? 'Beenden' : 'Reihenfolge' }}
                      </button>
                      <button
                        v-if="orderMode === 'financial'"
                        @click="saveFieldOrder('financial')"
                        class="btn btn-success"
                        :disabled="savingOrder"
                      >
                        <font-awesome-icon icon="save" class="me-1" />
                        {{ savingOrder ? 'Speichern...' : 'Speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="card mb-3">
                    <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div>
                        <h6 class="mb-0">Gruppen für Finanzfelder</h6>
                        <small class="text-muted">Lege Standard-Gruppen für finanzielle Angaben fest.</small>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="addFieldGroup('financial')"
                        >
                          <font-awesome-icon icon="layer-group" class="me-1" />
                          Gruppe hinzufügen
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <div v-if="getFieldGroups('financial').length === 0" class="text-muted small">
                        Noch keine Gruppen vorhanden. Füge eine Gruppe hinzu, um die Übersicht zu verbessern.
                      </div>
                      <div v-else class="field-group-list">
                        <div
                          v-for="(group, index) in getFieldGroups('financial')"
                          :key="group.id"
                          class="field-group-item border rounded p-3 mb-2"
                        >
                          <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3">
                            <div class="d-flex align-items-center gap-2 flex-grow-1 w-100">
                              <span class="group-color-dot" :style="{ backgroundColor: group.color }"></span>
                              <input
                                type="text"
                                class="form-control"
                                v-model="group.name"
                                placeholder="Gruppenname"
                                @input="markFieldGroupsDirty()"
                              />
                            </div>
                            <div class="d-flex align-items-center gap-3 flex-wrap">
                              <div class="d-flex align-items-center gap-2">
                                <label class="form-label small mb-0 text-muted">Farbe</label>
                                <input
                                  type="color"
                                  class="form-control form-control-color"
                                  v-model="group.color"
                                  @input="markFieldGroupsDirty()"
                                  aria-label="Gruppenfarbe wählen"
                                />
                              </div>
                              <span class="badge bg-light text-dark">
                                {{ countFieldsInGroup('financial', group.id) }} Feld(er)
                              </span>
                              <div class="btn-group btn-group-sm" role="group" aria-label="Gruppenreihenfolge">
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('financial', index, -1)"
                                  :disabled="index === 0"
                                  title="Nach oben"
                                >
                                  <font-awesome-icon icon="arrow-up" />
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-outline-secondary"
                                  @click="moveFieldGroup('financial', index, 1)"
                                  :disabled="index === getFieldGroups('financial').length - 1"
                                  title="Nach unten"
                                >
                                  <font-awesome-icon icon="arrow-down" />
                                </button>
                              </div>
                              <button
                                type="button"
                                class="btn btn-outline-danger btn-sm"
                                @click="removeFieldGroup('financial', group)"
                                title="Gruppe löschen"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                      <small class="text-muted">
                        Änderungen an Gruppen müssen gespeichert werden, damit sie im Wizard erscheinen.
                      </small>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="saveTemplateFieldGroups"
                        :disabled="!fieldGroupsDirty || savingFieldGroups"
                      >
                        <span v-if="savingFieldGroups" class="spinner-border spinner-border-sm me-2"></span>
                        {{ savingFieldGroups ? 'Speichern...' : 'Gruppen speichern' }}
                      </button>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th v-if="orderMode === 'financial'" style="width: 50px">#</th>
                          <th>Name</th>
                          <th>Typ</th>
                          <th>Erforderlich</th>
                          <th>Gruppe</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(field, index) in getFieldsByCategory('financial')"
                          :key="field.id"
                          :class="{ 'table-warning': orderMode === 'financial' }"
                          :draggable="orderMode === 'financial'"
                          @dragstart="dragStart($event, field, 'financial')"
                          @dragover.prevent
                          @drop="drop($event, field, 'financial')"
                        >
                          <td v-if="orderMode === 'financial'" class="text-center">
                            <font-awesome-icon icon="grip-vertical" class="text-muted" />
                          </td>
                          <td>
                            <strong>{{ field.displayName }}</strong>
                            <br />
                            <small class="text-muted">{{ field.name }}</small>
                            <div class="mt-1">
                              <span v-if="field.isAdminField" class="badge bg-primary me-2">
                                <font-awesome-icon icon="shield-alt" class="me-1" />
                                Admin
                              </span>
                              <span v-if="field.isCalculated" class="badge bg-info me-2">
                                <font-awesome-icon icon="calculator" class="me-1" />
                                Berechnet
                              </span>
                              <span v-if="field.undeletable" class="badge bg-warning text-dark">
                                <font-awesome-icon icon="lock" class="me-1" />
                                Fixed
                              </span>
                            </div>
                          </td>
                          <td>
                            <span class="badge bg-info">{{ getFieldTypeLabel(field.type) }}</span>
                          </td>
                          <td>
                            <span v-if="field.required" class="badge bg-danger">Erforderlich</span>
                            <span v-else class="text-muted">Optional</span>
                          </td>
                          <td>
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                              <span
                                class="group-color-dot"
                                v-if="field.options?.groupId"
                                :style="{ backgroundColor: getGroupColor('financial', field.options.groupId) }"
                                :title="getGroupName('financial', field.options.groupId)"
                              ></span>
                              <select
                                class="form-select form-select-sm"
                                :value="field.options?.groupId || ''"
                                :disabled="orderMode === 'financial' || updatingFieldGroupId === field.id"
                                @change="onFieldGroupAssignmentChange(field, 'financial', $event.target.value)"
                              >
                                <option value="">Keine Gruppe</option>
                                <option
                                  v-for="group in getFieldGroups('financial')"
                                  :key="group.id"
                                  :value="group.id"
                                >
                                  {{ group.name }}
                                </option>
                              </select>
                              <span
                                v-if="updatingFieldGroupId === field.id"
                                class="spinner-border spinner-border-sm text-muted"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </div>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm">
                              <button
                                @click="editField(field)"
                                class="btn btn-outline-primary"
                                title="Feld bearbeiten"
                                :disabled="orderMode === 'financial'"
                              >
                                <font-awesome-icon icon="edit" />
                              </button>
                              <button
                                @click="deleteField(field)"
                                class="btn btn-outline-danger"
                                :title="
                                  field.undeletable
                                    ? 'Feld löschen (Admin-Berechtigung erforderlich)'
                                    : 'Feld löschen'
                                "
                                :disabled="orderMode === 'financial'"
                              >
                                <font-awesome-icon icon="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showFieldsModal = false">
            Schließen
          </button>
        </div>
      </div>
    </div>
  </div></Teleport>

  <!-- Create Field Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showCreateFieldModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="createFieldModalLabel"
      aria-hidden="true"
    >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createFieldModalLabel">
            <font-awesome-icon icon="shield-alt" class="me-2" />
            Neues Feld erstellen
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showCreateFieldModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Admin Field Info -->
          <div class="alert alert-info mb-3">
            <font-awesome-icon icon="shield-alt" class="me-2" />
            <strong>Admin-Feld:</strong> Dieses Feld wird als Admin-Feld erstellt und kann nur von
            Administratoren bearbeitet werden.
          </div>

          <form @submit.prevent="createField">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldName" class="form-label">Name *</label>
                  <input
                    v-model="newField.name"
                    type="text"
                    class="form-control"
                    id="fieldName"
                    placeholder="z.B. customer_name"
                    required
                  />
                  <div class="form-text">Technischer Name (keine Leerzeichen)</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldDisplayName" class="form-label">Anzeigename *</label>
                  <input
                    v-model="newField.displayName"
                    type="text"
                    class="form-control"
                    id="fieldDisplayName"
                    placeholder="z.B. Kundename"
                    required
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldType" class="form-label">Typ *</label>
                  <select v-model="newField.type" class="form-select" id="fieldType" required>
                    <option v-if="newField.category !== 'financial'" value="text">Text</option>
                    <option value="number">Zahl</option>
                    <option v-if="newField.category !== 'financial'" value="date">Datum</option>
                    <option v-if="newField.category !== 'financial'" value="select">
                      Auswahlliste
                    </option>
                    <option v-if="newField.category !== 'financial'" value="checkbox">
                      Checkbox
                    </option>
                    <option v-if="newField.category !== 'financial'" value="textarea">
                      Textbereich
                    </option>
                    <option v-if="newField.category !== 'financial'" value="file">Datei</option>
                    <option v-if="newField.category === 'financial'" value="money">
                      Geldbetrag
                    </option>
                    <option v-if="newField.category !== 'financial'" value="location">
                      Standort
                    </option>
                    <option v-if="newField.category === 'receipt'" value="repeater">
                      Repeater (Wiederholbare Positionen)
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldCategory" class="form-label">Kategorie *</label>
                  <select
                    v-model="newField.category"
                    class="form-select"
                    id="fieldCategory"
                    required
                  >
                    <option value="receipt">Quittung</option>
                    <option value="financial">Finanziell</option>
                    <option value="personal">Persönlich</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldPlaceholder" class="form-label">Placeholder</label>
                  <input
                    v-model="newField.placeholder"
                    type="text"
                    class="form-control"
                    id="fieldPlaceholder"
                    placeholder="z.B. Kundename eingeben..."
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="fieldDefaultValue" class="form-label">Standardwert</label>
                  <input
                    v-model="newField.defaultValue"
                    type="text"
                    class="form-control"
                    id="fieldDefaultValue"
                    placeholder="Standardwert..."
                  />
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="fieldDescription" class="form-label">Beschreibung</label>
              <textarea
                v-model="newField.description"
                class="form-control"
                id="fieldDescription"
                rows="2"
                placeholder="Beschreibung des Feldes..."
              ></textarea>
            </div>
            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="newField.required"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldRequired"
                />
                <label class="form-check-label" for="fieldRequired"> Erforderlich </label>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="newField.allowPrefill"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldAllowPrefill"
                />
                <label class="form-check-label" for="fieldAllowPrefill">
                  Im Profil vorausfüllbar
                </label>
                <div class="form-text">
                  Aktiviert, dass Nutzer dieses Feld in ihrem Profil vorbereiten können.
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="newField.undeletable"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldUndeletable"
                />
                <label class="form-check-label" for="fieldUndeletable">
                  <font-awesome-icon icon="lock" class="me-1" />
                  Fixed (nur Admin löschbar)
                </label>
                <div class="form-text">
                  Das Feld kann von normalen Nutzern nicht gelöscht werden. Als Administrator können
                  du es trotzdem löschen.
                </div>
              </div>
            </div>

            <!-- Calculated Field Options (only for money type) -->
            <div v-if="newField.type === 'money'" class="mb-3">
              <div class="form-check">
                <input
                  v-model="newField.isCalculated"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldIsCalculated"
                />
                <label class="form-check-label" for="fieldIsCalculated">
                  Berechnetes Feld (Preis pro Einheit)
                </label>
                <div class="form-text">
                  Das Feld wird automatisch basierend auf einer Menge und einem Preis pro Einheit
                  berechnet
                </div>
              </div>
            </div>

            <!-- Per Unit Calculation Configuration -->
            <div v-if="newField.isCalculated && newField.type === 'money'" class="mb-3">
              <div class="row">
                <div class="col-md-6">
                  <label for="fieldUnit" class="form-label">Einheit *</label>
                  <input
                    v-model="newField.options.unit"
                    type="text"
                    class="form-control"
                    id="fieldUnit"
                    placeholder="z.B. km, Stunde, Stück"
                    required
                  />
                </div>
                <div class="col-md-6">
                  <label for="fieldPricePerUnit" class="form-label">Preis pro Einheit *</label>
                  <input
                    v-model.number="newField.options.pricePerUnit"
                    type="number"
                    class="form-control"
                    id="fieldPricePerUnit"
                    step="0.01"
                    placeholder="0.30"
                    required
                  />
                </div>
              </div>
            </div>

            <!-- Select Options Configuration -->
            <div v-if="newField.type === 'select'" class="mb-3">
              <label for="selectOptions" class="form-label">Auswahloptionen *</label>
              <textarea
                v-model="selectOptionsText"
                class="form-control"
                id="selectOptions"
                rows="3"
                placeholder="Eine Option pro Zeile eingeben..."
                @input="updateSelectOptions"
              ></textarea>
              <div class="form-text">
                Gib jede Option in einer neuen Zeile ein. Diese werden als
                Auswahlmöglichkeiten angezeigt.
              </div>
            </div>

            <!-- Checkbox Default Value -->
            <div v-if="newField.type === 'checkbox'" class="mb-3">
              <div class="form-check">
                <input
                  v-model="newField.defaultValue"
                  class="form-check-input"
                  type="checkbox"
                  id="checkboxDefaultValue"
                />
                <label for="checkboxDefaultValue" class="form-check-label">
                  Standardmäßig aktiviert
                </label>
              </div>
              <div class="form-text">Bestimmt, ob die Checkbox standardmäßig aktiviert ist.</div>
            </div>

            <!-- Min/Max Values for Financial Fields -->
            <div v-if="newField.category === 'financial'" class="mb-3">
              <label class="form-label">Wertbereich</label>
              <div class="row">
                <div class="col-md-6">
                  <label for="fieldMinValue" class="form-label">Mindestwert</label>
                  <input
                    v-model.number="newField.validation.min"
                    type="number"
                    class="form-control"
                    id="fieldMinValue"
                    step="0.01"
                    placeholder="z.B. 0.00"
                  />
                  <div class="form-text">Mindestwert für dieses Feld</div>
                </div>
                <div class="col-md-6">
                  <label for="fieldMaxValue" class="form-label">Höchstwert</label>
                  <input
                    v-model.number="newField.validation.max"
                    type="number"
                    class="form-control"
                    id="fieldMaxValue"
                    step="0.01"
                    placeholder="z.B. 9999.99"
                  />
                  <div class="form-text">Höchstwert für dieses Feld</div>
                </div>
              </div>
            </div>

            <!-- Tax Rate Options for Money/Financial Fields -->
            <div v-if="newField.type === 'money' && newField.category === 'financial'" class="mb-3">
              <label class="form-label">Steuersatz-Optionen</label>
              <div class="form-check">
                <input
                  v-model="newField.options.includeTaxRate"
                  class="form-check-input"
                  type="checkbox"
                  id="fieldIncludeTaxRate"
                />
                <label class="form-check-label" for="fieldIncludeTaxRate">
                  Steuersatz-Auswahl anzeigen
                </label>
                <div class="form-text">
                  Benutzer können einen Steuersatz für dieses Feld auswählen
                </div>
              </div>
              <div v-if="newField.options.includeTaxRate" class="mt-2">
                <label class="form-label">Verfügbare Steuersätze</label>
                <div
                  v-for="taxRate in taxRates.filter((t) => t.isActive)"
                  :key="taxRate.id"
                  class="form-check"
                >
                  <input
                    :value="taxRate.id"
                    v-model="newField.options.availableTaxRates"
                    class="form-check-input"
                    type="checkbox"
                    :id="`taxRate-${taxRate.id}`"
                  />
                  <label class="form-check-label" :for="`taxRate-${taxRate.id}`">
                    {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                  </label>
                </div>
              </div>
            </div>

            <!-- Repeater Field Configuration -->
            <div v-if="newField.type === 'repeater'" class="mb-3">
              <label class="form-label">Repeater-Konfiguration</label>
              <div class="alert alert-info">
                <font-awesome-icon icon="info-circle" class="me-2" />
                <strong>Repeater-Feld:</strong> Ermöglicht das Hinzufügen mehrerer Positionen mit folgenden Feldern:
                <ul class="mb-0 mt-2">
                  <li>Anzahl (Zahlenfeld)</li>
                  <li>Einheit (Textfeld, optional)</li>
                  <li>Beschreibung (Textfeld)</li>
                  <li>Einzelpreis (Geldbetrag)</li>
                  <li>Steuersatz (optional)</li>
                </ul>
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <label for="repeaterMinItems" class="form-label">Mindestanzahl Positionen</label>
                  <input
                    v-model.number="newField.options.repeaterMinItems"
                    type="number"
                    class="form-control"
                    id="repeaterMinItems"
                    min="0"
                    placeholder="0"
                  />
                  <div class="form-text">Mindestanzahl der Positionen (0 = keine Mindestanzahl)</div>
                </div>
                <div class="col-md-6">
                  <label for="repeaterMaxItems" class="form-label">Höchstanzahl Positionen</label>
                  <input
                    v-model.number="newField.options.repeaterMaxItems"
                    type="number"
                    class="form-control"
                    id="repeaterMaxItems"
                    min="1"
                    placeholder="10"
                  />
                  <div class="form-text">Höchstanzahl der Positionen (leer = unbegrenzt)</div>
                </div>
              </div>
              
              <div class="mt-3">
                <div class="form-check">
                  <input
                    v-model="newField.options.repeaterIncludeTaxRate"
                    class="form-check-input"
                    type="checkbox"
                    id="repeaterIncludeTaxRate"
                  />
                  <label class="form-check-label" for="repeaterIncludeTaxRate">
                    Steuersatz-Auswahl für Einzelpreise
                  </label>
                  <div class="form-text">
                    Ermöglicht die Auswahl eines Steuersatzes für jeden Einzelpreis
                  </div>
                </div>
              </div>
              
              <div v-if="newField.options.repeaterIncludeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select
                  v-model="newField.options.repeaterTaxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Die Positionen werden ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="newField.options.repeaterTaxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div
                    v-for="taxRate in taxRates.filter((t) => t.isActive)"
                    :key="taxRate.id"
                    class="form-check"
                  >
                    <input
                      :value="taxRate.id"
                      v-model="newField.options.repeaterAvailableTaxRates"
                      class="form-check-input"
                      type="checkbox"
                      :id="`repeaterTaxRate-${taxRate.id}`"
                    />
                    <label class="form-check-label" :for="`repeaterTaxRate-${taxRate.id}`">
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </label>
                  </div>
                </div>
                
                <!-- Fixed tax rate selection -->
                <div v-if="newField.options.repeaterTaxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select
                    v-model="newField.options.repeaterSelectedTaxRateId"
                    class="form-select"
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option
                      v-for="taxRate in taxRates.filter((t) => t.isActive)"
                      :key="taxRate.id"
                      :value="taxRate.id"
                    >
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showCreateFieldModal = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="createField"
            :disabled="creatingField"
          >
            <span v-if="creatingField" class="spinner-border spinner-border-sm me-2"></span>
            {{ creatingField ? 'Wird erstellt...' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div></Teleport>

  <!-- Edit Field Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showEditFieldModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="editFieldModalLabel"
      aria-hidden="true"
    >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editFieldModalLabel">
            <font-awesome-icon icon="edit" class="me-2" />
            Feld bearbeiten
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showEditFieldModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Admin Field Notice -->
          <div v-if="editingField.isAdminField" class="alert alert-info mb-3">
            <font-awesome-icon icon="shield-alt" class="me-2" />
            <strong>Admin-Feld:</strong> Dieses Feld wurde von einem Administrator erstellt und kann
            nur von Administratoren bearbeitet werden.
          </div>

          <form @submit.prevent="updateField">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldName" class="form-label">Name *</label>
                  <input
                    v-model="editingField.name"
                    type="text"
                    class="form-control"
                    id="editFieldName"
                    required
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldDisplayName" class="form-label">Anzeigename *</label>
                  <input
                    v-model="editingField.displayName"
                    type="text"
                    class="form-control"
                    id="editFieldDisplayName"
                    required
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldType" class="form-label">Typ *</label>
                  <select
                    v-model="editingField.type"
                    class="form-select"
                    id="editFieldType"
                    required
                  >
                    <option v-if="editingField.category !== 'financial'" value="text">Text</option>
                    <option value="number">Zahl</option>
                    <option v-if="editingField.category !== 'financial'" value="date">Datum</option>
                    <option v-if="editingField.category !== 'financial'" value="select">
                      Auswahlliste
                    </option>
                    <option v-if="editingField.category !== 'financial'" value="checkbox">
                      Checkbox
                    </option>
                    <option v-if="editingField.category !== 'financial'" value="textarea">
                      Textbereich
                    </option>
                    <option v-if="editingField.category !== 'financial'" value="file">Datei</option>
                    <option v-if="editingField.category === 'financial'" value="money">
                      Geldbetrag
                    </option>
                    <option v-if="editingField.category !== 'financial'" value="location">
                      Standort
                    </option>
                    <option v-if="editingField.category === 'receipt'" value="repeater">
                      Repeater (Wiederholbare Positionen)
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldCategory" class="form-label">Kategorie *</label>
                  <select
                    v-model="editingField.category"
                    class="form-select"
                    id="editFieldCategory"
                    required
                  >
                    <option value="receipt">Quittung</option>
                    <option value="financial">Finanziell</option>
                    <option value="personal">Persönlich</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldPlaceholder" class="form-label">Placeholder</label>
                  <input
                    v-model="editingField.placeholder"
                    type="text"
                    class="form-control"
                    id="editFieldPlaceholder"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editFieldDefaultValue" class="form-label">Standardwert</label>
                  <input
                    v-model="editingField.defaultValue"
                    type="text"
                    class="form-control"
                    id="editFieldDefaultValue"
                  />
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="editFieldDescription" class="form-label">Beschreibung</label>
              <textarea
                v-model="editingField.description"
                class="form-control"
                id="editFieldDescription"
                rows="2"
              ></textarea>
            </div>
            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingField.required"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldRequired"
                />
                <label class="form-check-label" for="editFieldRequired"> Erforderlich </label>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingField.allowPrefill"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldAllowPrefill"
                />
                <label class="form-check-label" for="editFieldAllowPrefill">
                  Im Profil vorausfüllbar
                </label>
                <div class="form-text">
                  Aktiviert, dass Nutzer dieses Feld in ihrem Profil vorbereiten können.
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingField.undeletable"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldUndeletable"
                />
                <label class="form-check-label" for="editFieldUndeletable">
                  <font-awesome-icon icon="lock" class="me-1" />
                  Fixed (nur Admin löschbar)
                </label>
                <div class="form-text">
                  Das Feld kann von normalen Nutzern nicht gelöscht werden. Administratoren können
                  es trotzdem löschen.
                </div>
              </div>
            </div>

            <!-- Calculated Field Options -->
            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingField.isCalculated"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldIsCalculated"
                />
                <label class="form-check-label" for="editFieldIsCalculated">
                  Berechnetes Feld
                </label>
                <div class="form-text">
                  Das Feld wird automatisch basierend auf anderen Feldern berechnet
                </div>
              </div>
            </div>

            <!-- Calculation Configuration -->
            <div v-if="editingField.isCalculated" class="mb-3">
              <label class="form-label">Berechnungsart</label>
              <select
                v-model="editingField.options.calculationType"
                class="form-select"
                id="editCalculationType"
              >
                <option value="perUnit">Pro Einheit (z.B. 0,30€ pro km)</option>
              </select>

              <!-- Per Unit Calculation -->
              <div v-if="editingField.options.calculationType === 'perUnit'" class="mt-3">
                <div class="row">
                  <div class="col-md-6">
                    <label for="editFieldUnit" class="form-label">Einheit</label>
                    <input
                      v-model="editingField.options.unit"
                      type="text"
                      class="form-control"
                      id="editFieldUnit"
                      placeholder="z.B. km, Stunde, Stück"
                    />
                  </div>
                  <div class="col-md-6">
                    <label for="editFieldPricePerUnit" class="form-label">Preis pro Einheit</label>
                    <input
                      v-model.number="editingField.options.pricePerUnit"
                      type="number"
                      class="form-control"
                      id="editFieldPricePerUnit"
                      step="0.01"
                      placeholder="0.30"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Select Options Configuration -->
            <div v-if="editingField.type === 'select'" class="mb-3">
              <label for="editSelectOptions" class="form-label">Auswahloptionen *</label>
              <textarea
                v-model="editSelectOptionsText"
                class="form-control"
                id="editSelectOptions"
                rows="3"
                placeholder="Eine Option pro Zeile eingeben..."
                @input="updateEditSelectOptions"
              ></textarea>
              <div class="form-text">
                Gib jede Option in einer neuen Zeile ein. Diese werden als
                Auswahlmöglichkeiten angezeigt.
              </div>
            </div>

            <!-- Checkbox Default Value -->
            <div v-if="editingField.type === 'checkbox'" class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingField.defaultValue"
                  class="form-check-input"
                  type="checkbox"
                  id="editCheckboxDefaultValue"
                />
                <label for="editCheckboxDefaultValue" class="form-check-label">
                  Standardmäßig aktiviert
                </label>
              </div>
              <div class="form-text">Bestimmt, ob die Checkbox standardmäßig aktiviert ist.</div>
            </div>

            <!-- Min/Max Values for Financial Fields -->
            <div v-if="editingField.category === 'financial'" class="mb-3">
              <label class="form-label">Wertbereich</label>
              <div class="row">
                <div class="col-md-6">
                  <label for="editFieldMinValue" class="form-label">Mindestwert</label>
                  <input
                    v-model.number="editingField.validation.min"
                    type="number"
                    class="form-control"
                    id="editFieldMinValue"
                    step="0.01"
                    placeholder="z.B. 0.00"
                  />
                  <div class="form-text">Mindestwert für dieses Feld</div>
                </div>
                <div class="col-md-6">
                  <label for="editFieldMaxValue" class="form-label">Höchstwert</label>
                  <input
                    v-model.number="editingField.validation.max"
                    type="number"
                    class="form-control"
                    id="editFieldMaxValue"
                    step="0.01"
                    placeholder="z.B. 9999.99"
                  />
                  <div class="form-text">Höchstwert für dieses Feld</div>
                </div>
              </div>
            </div>

            <!-- Tax Rate Options for Money/Financial Fields -->
            <div
              v-if="editingField.type === 'money' && editingField.category === 'financial'"
              class="mb-3"
            >
              <label class="form-label">Steuersatz-Optionen</label>
              <div class="form-check">
                <input
                  v-model="editingField.options.includeTaxRate"
                  class="form-check-input"
                  type="checkbox"
                  id="editFieldIncludeTaxRate"
                />
                <label class="form-check-label" for="editFieldIncludeTaxRate">
                  Steuersatz-Auswahl anzeigen
                </label>
                <div class="form-text">
                  Benutzer können einen Steuersatz für dieses Feld auswählen
                </div>
              </div>
              <div v-if="editingField.options.includeTaxRate" class="mt-2">
                <label class="form-label">Verfügbare Steuersätze</label>
                <div
                  v-for="taxRate in taxRates.filter((t) => t.isActive)"
                  :key="taxRate.id"
                  class="form-check"
                >
                  <input
                    :value="taxRate.id"
                    v-model="editingField.options.availableTaxRates"
                    class="form-check-input"
                    type="checkbox"
                    :id="`editTaxRate-${taxRate.id}`"
                  />
                  <label class="form-check-label" :for="`editTaxRate-${taxRate.id}`">
                    {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                  </label>
                </div>
              </div>
            </div>

            <!-- Repeater Field Configuration -->
            <div v-if="editingField.type === 'repeater'" class="mb-3">
              <label class="form-label">Repeater-Konfiguration</label>
              <div class="alert alert-info">
                <font-awesome-icon icon="info-circle" class="me-2" />
                <strong>Repeater-Feld:</strong> Ermöglicht das Hinzufügen mehrerer Positionen mit folgenden Feldern:
                <ul class="mb-0 mt-2">
                  <li>Anzahl (Zahlenfeld)</li>
                  <li>Einheit (Textfeld, optional)</li>
                  <li>Beschreibung (Textfeld)</li>
                  <li>Einzelpreis (Geldbetrag)</li>
                  <li>Steuersatz (optional)</li>
                </ul>
              </div>
              
              <div class="row">
                <div class="col-md-6">
                  <label for="editRepeaterMinItems" class="form-label">Mindestanzahl Positionen</label>
                  <input
                    v-model.number="editingField.options.repeaterMinItems"
                    type="number"
                    class="form-control"
                    id="editRepeaterMinItems"
                    min="0"
                    placeholder="0"
                  />
                  <div class="form-text">Mindestanzahl der Positionen (0 = keine Mindestanzahl)</div>
                </div>
                <div class="col-md-6">
                  <label for="editRepeaterMaxItems" class="form-label">Höchstanzahl Positionen</label>
                  <input
                    v-model.number="editingField.options.repeaterMaxItems"
                    type="number"
                    class="form-control"
                    id="editRepeaterMaxItems"
                    min="1"
                    placeholder="10"
                  />
                  <div class="form-text">Höchstanzahl der Positionen (leer = unbegrenzt)</div>
                </div>
              </div>
              
              <div class="mt-3">
                <div class="form-check">
                  <input
                    v-model="editingField.options.repeaterIncludeTaxRate"
                    class="form-check-input"
                    type="checkbox"
                    id="editRepeaterIncludeTaxRate"
                  />
                  <label class="form-check-label" for="editRepeaterIncludeTaxRate">
                    Steuersatz-Auswahl für Einzelpreise
                  </label>
                  <div class="form-text">
                    Ermöglicht die Auswahl eines Steuersatzes für jeden Einzelpreis
                  </div>
                </div>
              </div>
              
              <div v-if="editingField.options.repeaterIncludeTaxRate" class="mt-3">
                <label class="form-label">Steuersatz-Auswahl</label>
                <select
                  v-model="editingField.options.repeaterTaxRateSelectionMode"
                  class="form-select"
                >
                  <option value="user_choice">Benutzer wählt Steuersatz</option>
                  <option value="fixed">Fester Steuersatz</option>
                  <option value="none">Kein Steuersatz</option>
                </select>
                <div class="form-text">
                  <strong>Benutzer wählt:</strong> Der Benutzer kann aus verfügbaren Steuersätzen wählen<br>
                  <strong>Fester Steuersatz:</strong> Ein spezifischer Steuersatz wird automatisch angewendet<br>
                  <strong>Kein Steuersatz:</strong> Die Positionen werden ohne Steuersatz behandelt
                </div>

                <!-- Auswahl der verfügbaren Steuersätze (nur bei user_choice) -->
                <div v-if="editingField.options.repeaterTaxRateSelectionMode === 'user_choice'" class="mt-3">
                  <label class="form-label">Verfügbare Steuersätze</label>
                  <div
                    v-for="taxRate in taxRates.filter((t) => t.isActive)"
                    :key="taxRate.id"
                    class="form-check"
                  >
                    <input
                      :value="taxRate.id"
                      v-model="editingField.options.repeaterAvailableTaxRates"
                      class="form-check-input"
                      type="checkbox"
                      :id="`editRepeaterTaxRate-${taxRate.id}`"
                    />
                    <label class="form-check-label" :for="`editRepeaterTaxRate-${taxRate.id}`">
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </label>
                  </div>
                </div>
                
                <!-- Fixed tax rate selection -->
                <div v-if="editingField.options.repeaterTaxRateSelectionMode === 'fixed'" class="mt-3">
                  <label class="form-label">Festen Steuersatz wählen</label>
                  <select
                    v-model="editingField.options.repeaterSelectedTaxRateId"
                    class="form-select"
                  >
                    <option value="">-- Steuersatz auswählen --</option>
                    <option
                      v-for="taxRate in taxRates.filter((t) => t.isActive)"
                      :key="taxRate.id"
                      :value="taxRate.id"
                    >
                      {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showEditFieldModal = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="updateField"
            :disabled="updatingField"
          >
            <span v-if="updatingField" class="spinner-border spinner-border-sm me-2"></span>
            {{ updatingField ? 'Wird aktualisiert...' : 'Aktualisieren' }}
          </button>
        </div>
      </div>
    </div> </div>
  </Teleport>

  <!-- Modal Backdrops -->
  <div
    v-if="showFieldsModal"
    class="modal-backdrop fade show"
    @click="showFieldsModal = false"
  ></div>
  <div
    v-if="showCreateFieldModal"
    class="modal-backdrop fade show"
    @click="showCreateFieldModal = false"
  ></div>
  <div
    v-if="showEditFieldModal"
    class="modal-backdrop fade show"
    @click="showEditFieldModal = false"
  ></div>

  <!-- Tax Rates Management Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showTaxRatesModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="taxRatesModalLabel"
      aria-hidden="true"
    >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="taxRatesModalLabel">
            <font-awesome-icon icon="percentage" class="me-2" />
            Steuersätze verwalten
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showTaxRatesModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Loading -->
          <div v-if="loadingTaxRates" class="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Steuersätze werden geladen...</span>
            </div>
          </div>

          <!-- Tax Rates List -->
          <div v-else>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Steuersätze ({{ taxRates.length }})</h6>
              <button @click="showCreateTaxRateModal = true" class="btn btn-primary btn-sm">
                <font-awesome-icon icon="plus" class="me-1" />
                Neuer Steuersatz
              </button>
            </div>

            <div v-if="taxRates.length === 0" class="text-center py-4">
              <font-awesome-icon
                icon="percentage"
                class="text-muted mb-3"
                style="font-size: 3rem"
              />
              <p class="text-muted">Keine Steuersätze definiert</p>
              <button @click="showCreateTaxRateModal = true" class="btn btn-primary">
                <font-awesome-icon icon="plus" class="me-1" />
                Ersten Steuersatz hinzufügen
              </button>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Satz</th>
                    <th>Status</th>
                    <th>Beschreibung</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="taxRate in taxRates" :key="taxRate.id">
                    <td>
                      <strong>{{ taxRate.displayName }}</strong>
                      <br />
                      <small class="text-muted">{{ taxRate.name }}</small>
                    </td>
                    <td>
                      <span class="badge bg-info">{{ taxRate.rate }}%</span>
                    </td>
                    <td>
                      <span v-if="taxRate.isDefault" class="badge bg-warning text-dark">
                        <font-awesome-icon icon="star" class="me-1" />
                        Standard
                      </span>
                      <span v-else-if="taxRate.isActive" class="badge bg-success">Aktiv</span>
                      <span v-else class="badge bg-secondary">Inaktiv</span>
                    </td>
                    <td>
                      <small class="text-muted">{{ taxRate.description || '-' }}</small>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button
                          @click="editTaxRate(taxRate)"
                          class="btn btn-outline-primary"
                          title="Steuersatz bearbeiten"
                        >
                          <font-awesome-icon icon="edit" />
                        </button>
                        <button
                          v-if="!taxRate.isDefault"
                          @click="setDefaultTaxRate(taxRate)"
                          class="btn btn-outline-warning"
                          title="Als Standard setzen"
                        >
                          <font-awesome-icon icon="star" />
                        </button>
                        <button
                          v-if="!taxRate.isDefault"
                          @click="deleteTaxRate(taxRate)"
                          class="btn btn-outline-danger"
                          title="Steuersatz löschen"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showTaxRatesModal = false">
            Schließen
          </button>
        </div>
      </div>
    </div>
  </div></Teleport>

  <!-- Create Tax Rate Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showCreateTaxRateModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="createTaxRateModalLabel"
      aria-hidden="true"
    >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createTaxRateModalLabel">
            <font-awesome-icon icon="plus" class="me-2" />
            Neuen Steuersatz erstellen
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showCreateTaxRateModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createTaxRate">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="taxRateName" class="form-label">Name *</label>
                  <input
                    v-model="newTaxRate.name"
                    type="text"
                    class="form-control"
                    id="taxRateName"
                    placeholder="z.B. standard"
                    required
                  />
                  <div class="form-text">Technischer Name (keine Leerzeichen)</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="taxRateDisplayName" class="form-label">Anzeigename *</label>
                  <input
                    v-model="newTaxRate.displayName"
                    type="text"
                    class="form-control"
                    id="taxRateDisplayName"
                    placeholder="z.B. Standard (19%)"
                    required
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="taxRateRate" class="form-label">Steuersatz (%) *</label>
                  <input
                    v-model.number="newTaxRate.rate"
                    type="number"
                    class="form-control"
                    id="taxRateRate"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="19.00"
                    required
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="taxRateOrder" class="form-label">Sortierreihenfolge</label>
                  <input
                    v-model.number="newTaxRate.order"
                    type="number"
                    class="form-control"
                    id="taxRateOrder"
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="taxRateDescription" class="form-label">Beschreibung</label>
              <textarea
                v-model="newTaxRate.description"
                class="form-control"
                id="taxRateDescription"
                rows="2"
                placeholder="Beschreibung des Steuersatzes..."
              ></textarea>
            </div>
            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="newTaxRate.isDefault"
                  class="form-check-input"
                  type="checkbox"
                  id="taxRateIsDefault"
                />
                <label class="form-check-label" for="taxRateIsDefault"> Als Standard setzen </label>
              </div>
              <div class="form-check">
                <input
                  v-model="newTaxRate.isActive"
                  class="form-check-input"
                  type="checkbox"
                  id="taxRateIsActive"
                />
                <label class="form-check-label" for="taxRateIsActive"> Aktiv </label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showCreateTaxRateModal = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="createTaxRate"
            :disabled="creatingTaxRate"
          >
            <span v-if="creatingTaxRate" class="spinner-border spinner-border-sm me-2"></span>
            {{ creatingTaxRate ? 'Wird erstellt...' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div></Teleport>

  <!-- Edit Tax Rate Modal -->
  <Teleport to="#modalTarget">
    <div
      v-if="showEditTaxRateModal"
      class="modal show d-block"
      tabindex="-1"
      aria-labelledby="editTaxRateModalLabel"
      aria-hidden="true"
    >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editTaxRateModalLabel">
            <font-awesome-icon icon="edit" class="me-2" />
            Steuersatz bearbeiten
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showEditTaxRateModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateTaxRate">
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editTaxRateName" class="form-label">Name *</label>
                  <input
                    v-model="editingTaxRate.name"
                    type="text"
                    class="form-control"
                    id="editTaxRateName"
                    required
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editTaxRateDisplayName" class="form-label">Anzeigename *</label>
                  <input
                    v-model="editingTaxRate.displayName"
                    type="text"
                    class="form-control"
                    id="editTaxRateDisplayName"
                    required
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editTaxRateRate" class="form-label">Steuersatz (%) *</label>
                  <input
                    v-model.number="editingTaxRate.rate"
                    type="number"
                    class="form-control"
                    id="editTaxRateRate"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="editTaxRateOrder" class="form-label">Sortierreihenfolge</label>
                  <input
                    v-model.number="editingTaxRate.order"
                    type="number"
                    class="form-control"
                    id="editTaxRateOrder"
                  />
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="editTaxRateDescription" class="form-label">Beschreibung</label>
              <textarea
                v-model="editingTaxRate.description"
                class="form-control"
                id="editTaxRateDescription"
                rows="2"
              ></textarea>
            </div>
            <div class="mb-3">
              <div class="form-check">
                <input
                  v-model="editingTaxRate.isDefault"
                  class="form-check-input"
                  type="checkbox"
                  id="editTaxRateIsDefault"
                />
                <label class="form-check-label" for="editTaxRateIsDefault">
                  Als Standard setzen
                </label>
              </div>
              <div class="form-check">
                <input
                  v-model="editingTaxRate.isActive"
                  class="form-check-input"
                  type="checkbox"
                  id="editTaxRateIsActive"
                />
                <label class="form-check-label" for="editTaxRateIsActive"> Aktiv </label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showEditTaxRateModal = false">
            Abbrechen
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="updateTaxRate"
            :disabled="updatingTaxRate"
          >
            <span v-if="updatingTaxRate" class="spinner-border spinner-border-sm me-2"></span>
            {{ updatingTaxRate ? 'Wird aktualisiert...' : 'Aktualisieren' }}
          </button>
        </div>
      </div>
    </div>
  </div></Teleport>

  <!-- Additional Modal Backdrops -->
  <div
    v-if="showTaxRatesModal"
    class="modal-backdrop fade show"
    @click="showTaxRatesModal = false"
  ></div>
  <div
    v-if="showCreateTaxRateModal"
    class="modal-backdrop fade show"
    @click="showCreateTaxRateModal = false"
  ></div>
  <div
    v-if="showEditTaxRateModal"
    class="modal-backdrop fade show"
    @click="showEditTaxRateModal = false"
  ></div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { templateService } from '@/api/templates'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'

const loading = ref(false)
const error = ref(null)
const templates = ref([])
const categories = ref([])
const showCreateModal = ref(false)
const creating = ref(false)
const showCategoriesModal = ref(false)
const showNewCategoryForm = ref(false)
const showEditModal = ref(false)
const editingTemplate = ref({})
const updating = ref(false)
const showFieldsModal = ref(false)
const selectedTemplateForFields = ref(null)
const templateFields = ref([])
const loadingFields = ref(false)
const showCreateFieldModal = ref(false)
const showEditFieldModal = ref(false)
const creatingField = ref(false)
const editingField = ref({})
const updatingField = ref(false)
const showTaxRatesModal = ref(false)
const taxRates = ref([])
const loadingTaxRates = ref(false)
const showCreateTaxRateModal = ref(false)
const showEditTaxRateModal = ref(false)
const creatingTaxRate = ref(false)
const editingTaxRate = ref({})
const updatingTaxRate = ref(false)
const updatingSignature = ref({})

const orderMode = ref(null) 
const savingOrder = ref(false)
const draggedField = ref(null)
const draggedCategory = ref(null)
const activeTab = ref('receipt') 

const FIELD_GROUP_CATEGORIES = ['receipt', 'financial', 'personal']
const DEFAULT_GROUP_COLORS = ['#D71021', '#0D6EFD', '#6610F2', '#198754', '#FD7E14', '#6C757D', '#0DCAF0', '#6F42C1']
const fieldGroupsState = ref({
  receipt: [],
  financial: [],
  personal: [],
})
const fieldGroupsDirty = ref(false)
const savingFieldGroups = ref(false)
const updatingFieldGroupId = ref(null)

const newField = ref({
  name: '',
  displayName: '',
  type: 'text',
  category: 'receipt',
  required: false,
  allowPrefill: false,
  placeholder: '',
  description: '',
  defaultValue: '',
  isCalculated: false,
  calculationFormula: '',
  validation: {
    min: null,
    max: null
  },
  options: {
    includeTaxRate: false,
    availableTaxRates: [],
    unit: '',
    pricePerUnit: 0, 
    options: [],
    groupId: null,
    // Repeater options
    repeaterMinItems: 0,
    repeaterMaxItems: null,
    repeaterIncludeTaxRate: false,
    repeaterTaxRateSelectionMode: 'user_choice',
    repeaterAvailableTaxRates: [],
    repeaterSelectedTaxRateId: '',
  },
})


const selectOptionsText = ref('')
const editSelectOptionsText = ref('')

const newTaxRate = ref({
  name: '',
  displayName: '',
  rate: 19.0,
  description: '',
  isDefault: false,
  isActive: true,
  order: 0,
})

const newTemplate = ref({
  name: '',
  displayName: '',
  description: '',
  categoryId: '',
  isProFeature: false,
  allowCustomFields: true,
  
  personalCategoryName: '',
  personalCategoryIcon: '',
  receiptCategoryName: '',
  receiptCategoryIcon: '',
  financialCategoryName: '',
  financialCategoryIcon: '',
})

const newCategory = ref({
  name: '',
  displayName: '',
  description: '',
  color: '#6c757d',
  icon: 'folder',
})

const sortedTemplates = computed(() => {
  return [...templates.value].sort((a, b) => {
    
    if (a.isDefault && !b.isDefault) return -1
    if (!a.isDefault && b.isDefault) return 1
    
    return a.displayName.localeCompare(b.displayName)
  })
})

const hasNonProDefaultTemplate = computed(() => {
  return templates.value.some((template) => template.isDefault && !template.isProFeature)
})

const loadTemplates = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await templateService.getAllTemplates(true) 
    templates.value = response.data || []

    
    for (const template of templates.value) {
      try {
        template.fieldCount = template.adminFields.length || 0
        // Initialize needsSignature if not present (default to true)
        if (template.needsSignature === undefined) {
          template.needsSignature = true
        }
        if (!template.settings || typeof template.settings !== 'object') {
          template.settings = template.settings ? JSON.parse(JSON.stringify(template.settings)) : null
        }
      } catch (fieldsError) {
        console.warn(
          `Could not load field count for template ${template.id}:`,
          fieldsError.response?.data,
        )
        template.fieldCount = 0
      }
    }
  } catch (err) {
    console.error('Error loading templates:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Templates'
  } finally {
    loading.value = false
  }
}

const refreshSelectedTemplate = () => {
  if (!selectedTemplateForFields.value) return

  const updatedTemplate = templates.value.find(
    (template) => template.id === selectedTemplateForFields.value.id,
  )

  if (updatedTemplate) {
    selectedTemplateForFields.value = updatedTemplate
    if (showFieldsModal.value) {
      initializeFieldGroups()
    }
  }
}

const loadCategories = async () => {
  try {
    const response = await templateService.getAllCategories(true) 
    categories.value = response.data || []
  } catch (err) {
    console.error('Error loading categories:', err)
    showError('Fehler beim Laden der Kategorien')
  }
}

const createTemplate = async () => {
  if (!newTemplate.value.name || !newTemplate.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  creating.value = true

  try {
    await templateService.createTemplate({
      ...newTemplate.value,
      categoryId: newTemplate.value.categoryId || null,
      isActive: true,
      
      personalCategoryName: newTemplate.value.personalCategoryName || null,
      personalCategoryIcon: newTemplate.value.personalCategoryIcon || null,
      receiptCategoryName: newTemplate.value.receiptCategoryName || null,
      receiptCategoryIcon: newTemplate.value.receiptCategoryIcon || null,
      financialCategoryName: newTemplate.value.financialCategoryName || null,
      financialCategoryIcon: newTemplate.value.financialCategoryIcon || null,
    })

    showSuccess('Template erfolgreich erstellt')
    showCreateModal.value = false
    resetNewTemplate()
    await loadTemplates()
  } catch (err) {
    console.error('Error creating template:', err)
    showError(err.response?.data?.message || 'Fehler beim Erstellen des Templates')
  } finally {
    creating.value = false
  }
}

const editTemplate = (template) => {
  editingTemplate.value = { ...template }
  
  editingTemplate.value.originalId = template.id
  
  editingTemplate.value.categoryId = template.category?.id || ''
  showEditModal.value = true
}

const manageFields = async (template) => {
  selectedTemplateForFields.value = template
  showFieldsModal.value = true
}

const updateTemplate = async () => {
  if (!editingTemplate.value.name || !editingTemplate.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  
  if (editingTemplate.value.isDefault && editingTemplate.value.isProFeature) {
    showError('Standard-Templates können nicht als Pro-Feature markiert werden')
    return
  }

  
  if (editingTemplate.value.isDefault) {
    editingTemplate.value.isActive = true
  }

  updating.value = true

  try {
    const templateData = {
      name: editingTemplate.value.name,
      displayName: editingTemplate.value.displayName,
      description: editingTemplate.value.description,
      categoryId: editingTemplate.value.categoryId || null,
      isProFeature: editingTemplate.value.isProFeature,
      allowCustomFields: editingTemplate.value.allowCustomFields,
      isActive: editingTemplate.value.isActive,
      
      personalCategoryName: editingTemplate.value.personalCategoryName || null,
      personalCategoryIcon: editingTemplate.value.personalCategoryIcon || null,
      receiptCategoryName: editingTemplate.value.receiptCategoryName || null,
      receiptCategoryIcon: editingTemplate.value.receiptCategoryIcon || null,
      financialCategoryName: editingTemplate.value.financialCategoryName || null,
      financialCategoryIcon: editingTemplate.value.financialCategoryIcon || null,
    }

    await templateService.updateTemplate(editingTemplate.value.originalId, templateData)

    showSuccess('Template erfolgreich aktualisiert')
    showEditModal.value = false
    await loadTemplates()
    refreshSelectedTemplate()
  } catch (err) {
    console.error('Error updating template:', err)
    showError(err.response?.data?.message || 'Fehler beim Aktualisieren des Templates')
  } finally {
    updating.value = false
  }
}

const setAsDefault = async (template) => {
  if (template.isDefault) {
    showError('Dieses Template ist bereits das Standard-Template')
    return
  }

  if (template.isProFeature) {
    showError(
      'Pro-Feature Templates können nicht als Standard-Template gesetzt werden. Standard-Templates müssen für alle Benutzer (auch Free-Accounts) verfügbar sein.',
    )
    return
  }

  const confirmed = await showConfirmDialog(
    'Standard-Template ändern',
    `Möchtest du "${template.displayName}" als neues Standard-Template festlegen? Das aktuelle Standard-Template wird dadurch ersetzt.`,
  )

  if (!confirmed.isConfirmed) return

  try {
    await templateService.setDefaultTemplate(template.id)
    showSuccess(`"${template.displayName}" wurde als Standard-Template festgelegt`)
    await loadTemplates() 
  } catch (err) {
    console.error('Error setting default template:', err)
    showError(err.response?.data?.message || 'Fehler beim Setzen des Standard-Templates')
  }
}

const deleteTemplate = async (template) => {
  if (template.isDefault) {
    showError('Standard-Templates können nicht gelöscht werden')
    return
  }

  const confirmed = await showConfirmDialog(
    'Template löschen',
    `Möchtest du das Template "${template.displayName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
  )

  if (!confirmed.isConfirmed) return

  try {
    await templateService.deleteTemplate(template.id)
    showSuccess('Template erfolgreich gelöscht')
    await loadTemplates()
  } catch (err) {
    console.error('Error deleting template:', err)
    showError(err.response?.data?.message || 'Fehler beim Löschen des Templates')
  }
}

const createField = async () => {
  if (!newField.value.name || !newField.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  
  if (newField.value.isCalculated && newField.value.type === 'money') {
    if (!newField.value.options.unit) {
      showError('Einheit ist für berechnete Felder erforderlich')
      return
    }
    if (!newField.value.options.pricePerUnit || newField.value.options.pricePerUnit <= 0) {
      showError('Preis pro Einheit muss größer als 0 sein')
      return
    }
  }

  
  if (newField.value.undeletable) {
    
    const confirmed = await showConfirmDialog(
      'Fixed Feld erstellen',
      'Dieses Feld wird als "Fixed" markiert und kann von normalen Nutzern nicht gelöscht werden. Administratoren können es trotzdem löschen. Fortfahren?',
    )
    if (!confirmed.isConfirmed) return
  }

  creatingField.value = true

  try {
    await templateService.createAdminField(selectedTemplateForFields.value.id, newField.value)
    showSuccess('Admin-Feld erfolgreich erstellt')
    showCreateFieldModal.value = false
    await loadTemplates()
    refreshSelectedTemplate()

    
    newField.value = {
      name: '',
      displayName: '',
      type: 'text',
      category: 'receipt',
      required: false,
      allowPrefill: false,
      undeletable: false,
      placeholder: '',
      description: '',
      defaultValue: '',
      isCalculated: false,
      calculationFormula: '',
      options: {
        includeTaxRate: false,
        availableTaxRates: [],
        unit: '',
        pricePerUnit: 0,
        dependsOn: '',
      },
    }
  } catch (err) {
    console.error('Error creating admin field:', err)
    showError(err.response?.data?.error || 'Fehler beim Erstellen des Admin-Feldes')
  } finally {
    creatingField.value = false
  }
}

const editField = async (field) => {
  editingField.value = { ...field }
  
  if (!editingField.value.options) {
    editingField.value.options = {
      includeTaxRate: false,
      availableTaxRates: [],
      options: [],
      groupId: null,
      // Repeater options
      repeaterMinItems: 0,
      repeaterMaxItems: null,
      repeaterIncludeTaxRate: false,
      repeaterTaxRateSelectionMode: 'user_choice',
      repeaterAvailableTaxRates: [],
      repeaterSelectedTaxRateId: '',
    }
  }
  if (!Object.prototype.hasOwnProperty.call(editingField.value.options, 'groupId')) {
    editingField.value.options.groupId = null
  }
  
  if (!editingField.value.validation) {
    editingField.value.validation = {
      min: null,
      max: null
    }
  }

  if (typeof editingField.value.allowPrefill === 'undefined') {
    editingField.value.allowPrefill = false
  }

  
  initializeEditSelectOptions(field)

  
  if (field.isAdminField) {
    editingField.value.isAdminField = true
  }

  showEditFieldModal.value = true
  await loadTaxRates()
}

const updateField = async () => {
  if (!editingField.value.name || !editingField.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  
  if (editingField.value.isCalculated) {
    if (!editingField.value.calculationFormula) {
      editingField.value.calculationFormula = 'perUnit'
    }

    
    if (editingField.value.options.calculationType === 'perUnit') {
      if (!editingField.value.options.unit) {
        showError('Einheit ist für Pro-Einheit-Berechnung erforderlich')
        return
      }
      if (
        !editingField.value.options.pricePerUnit ||
        editingField.value.options.pricePerUnit <= 0
      ) {
        showError('Preis pro Einheit muss größer als 0 sein')
        return
      }
    }
  }

  
  if (editingField.value.undeletable) {
    
    const confirmed = await showConfirmDialog(
      'Fixed Feld bearbeiten',
      'Dieses Feld wird als "Fixed" markiert und kann von normalen Nutzern nicht gelöscht werden. Administratoren können es trotzdem löschen. Fortfahren?',
    )
    if (!confirmed.isConfirmed) return
  }

  updatingField.value = true

  try {
    await templateService.updateTemplateField(editingField.value.id, editingField.value)
    showSuccess('Admin-Feld erfolgreich aktualisiert')
    showEditFieldModal.value = false
    await loadTemplates()
    refreshSelectedTemplate()
  } catch (err) {
    console.error('Error updating admin field:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Admin-Feldes')
  } finally {
    updatingField.value = false
  }
}

const deleteField = async (field) => {
  
  
  
  if (field.undeletable) {
    const confirmed = await showConfirmDialog(
      'Fixed Feld löschen',
      `Das Feld "${field.displayName}" ist als "Fixed" markiert. Als Administrator kannst du es trotzdem löschen. Fortfahren?`,
    )
    if (!confirmed.isConfirmed) return
  }

  
  if (field.isAdminField) {
    const confirmed = await showConfirmDialog(
      'Admin-Feld löschen',
      `Das Feld "${field.displayName}" ist ein Admin-Feld. Möchtest du es wirklich löschen?`,
    )
    if (!confirmed.isConfirmed) return
  }

  const confirmed = await showConfirmDialog(
    'Feld löschen',
    `Möchtest du das Feld "${field.displayName}" wirklich löschen?`,
  )

  if (!confirmed.isConfirmed) return

  try {
    
    if (field.isAdminField) {
      
      
    }

    await templateService.deleteTemplateField(field.id)
    showSuccess('Admin-Feld erfolgreich gelöscht')
    await loadTemplates()
    refreshSelectedTemplate()
  } catch (err) {
    console.error('Error deleting admin field:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen des Admin-Feldes')
  }
}

const getFieldTypeLabel = (type) => {
  const typeLabels = {
    text: 'Text',
    number: 'Zahl',
    date: 'Datum',
    select: 'Auswahl',
    checkbox: 'Checkbox',
    textarea: 'Textbereich',
    file: 'Datei',
    money: 'Geldbetrag',
    location: 'Standort',
  }
  return typeLabels[type] || type
}

const getFieldCategoryLabel = (category) => {
  const categoryLabels = {
    personal: 'Persönlich',
    receipt: 'Quittung',
    financial: 'Finanziell',
  }
  return categoryLabels[category] || category
}

const getCalculationDescription = (field) => {
  if (!field.isCalculated) return ''

  const calculationType = field.options?.calculationType || 'simple'

  switch (calculationType) {
    case 'perUnit':
      const unit = field.options?.unit || ''
      const price = field.options?.pricePerUnit || 0
      const dependsOn = field.options?.dependsOn?.[0] || ''
      return `${price}€ pro ${unit} (abhängig von: ${dependsOn})`
    case 'custom':
      return `Formel: ${field.calculationFormula}`
    case 'simple':
      return `Formel: ${field.calculationFormula}`
    default:
      return 'Berechnetes Feld'
  }
}


const getFieldsByCategory = (category) => {
  if (!selectedTemplateForFields.value?.adminFields) return []

  return selectedTemplateForFields.value.adminFields
    .filter((field) => field.category === category)
    .sort((a, b) => {
      
      if (a.order !== b.order) {
        return (a.order || 0) - (b.order || 0)
      }
      return (a.displayName || '').localeCompare(b.displayName || '')
    })
}

const generateGroupId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `grp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

const getFieldGroups = (category) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return []
  }
  return fieldGroupsState.value[category] || []
}

const countFieldsInGroup = (category, groupId) => {
  if (!groupId) {
    return 0
  }
  return getFieldsByCategory(category).filter(
    (field) => (field.options?.groupId || null) === groupId,
  ).length
}

const getGroupColor = (category, groupId) => {
  const group = getFieldGroups(category).find((item) => item.id === groupId)
  return group?.color || '#E9ECEF'
}

const getGroupName = (category, groupId) => {
  const group = getFieldGroups(category).find((item) => item.id === groupId)
  return group?.name || ''
}

const markFieldGroupsDirty = () => {
  fieldGroupsDirty.value = true
}

const initializeFieldGroups = () => {
  if (!selectedTemplateForFields.value) {
    fieldGroupsState.value = { receipt: [], financial: [], personal: [] }
    fieldGroupsDirty.value = false
    return
  }

  const rawGroups = selectedTemplateForFields.value.settings?.fieldGroups || {}
  const cloneCategory = (category) => {
    const groups = Array.isArray(rawGroups?.[category]) ? rawGroups[category] : []
    return groups
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((group, index) => ({
        id: group.id || generateGroupId(),
        name: group.name || '',
        color: group.color || '#E9ECEF',
        order: index,
      }))
  }

  fieldGroupsState.value = {
    receipt: cloneCategory('receipt'),
    financial: cloneCategory('financial'),
    personal: cloneCategory('personal'),
  }
  if (Array.isArray(selectedTemplateForFields.value?.adminFields)) {
    selectedTemplateForFields.value.adminFields.forEach((field) => {
      if (!field.options || typeof field.options !== 'object') {
        field.options = {}
      }
      if (!Object.prototype.hasOwnProperty.call(field.options, 'groupId')) {
        field.options.groupId = null
      }
    })
  }
  fieldGroupsDirty.value = false
}

const addFieldGroup = (category) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const existing = getFieldGroups(category)
  const color = DEFAULT_GROUP_COLORS[existing.length % DEFAULT_GROUP_COLORS.length] || '#E9ECEF'
  const newGroup = {
    id: generateGroupId(),
    name: existing.length === 0 ? 'Neue Gruppe' : `Neue Gruppe ${existing.length + 1}`,
    color,
    order: existing.length,
  }

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: [...existing, newGroup],
  }
  markFieldGroupsDirty()
}

const moveFieldGroup = (category, index, direction) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const groups = [...getFieldGroups(category)]
  const targetIndex = index + direction
  if (targetIndex < 0 || targetIndex >= groups.length) {
    return
  }

  const [movedGroup] = groups.splice(index, 1)
  groups.splice(targetIndex, 0, movedGroup)

  const reordered = groups.map((group, idx) => ({
    ...group,
    order: idx,
  }))

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: reordered,
  }
  markFieldGroupsDirty()
}

const clearFieldGroupAssignments = async (category, groupId) => {
  const fields = getFieldsByCategory(category)
  const affected = fields.filter((field) => (field.options?.groupId || null) === groupId)

  if (affected.length === 0) {
    return
  }

  try {
    await Promise.all(
      affected.map(async (field) => {
        const currentOptions = field.options ? { ...field.options } : {}
        currentOptions.groupId = null
        await templateService.updateTemplateField(field.id, { options: currentOptions })
        field.options = currentOptions
        const templateField = selectedTemplateForFields.value?.adminFields?.find(
          (item) => item.id === field.id,
        )
        if (templateField) {
          templateField.options = currentOptions
        }
      }),
    )
    showSuccess('Felder aus gelöschter Gruppe entfernt')
  } catch (err) {
    console.error('Error clearing field group assignments:', err)
    showError(err.response?.data?.error || 'Fehler beim Entfernen der Feld-Gruppen')
  }
}

const removeFieldGroup = async (category, group) => {
  if (!FIELD_GROUP_CATEGORIES.includes(category) || !group) {
    return
  }

  const confirmation = await showConfirmDialog(
    'Gruppe löschen',
    `Möchtest du die Gruppe "${group.name}" wirklich löschen? Zugeordnete Felder werden aus der Gruppe entfernt.`,
    'Gruppe löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545',
    },
  )

  if (!confirmation.isConfirmed) {
    return
  }

  const remaining = getFieldGroups(category)
    .filter((existing) => existing.id !== group.id)
    .map((existing, index) => ({
      ...existing,
      order: index,
    }))

  fieldGroupsState.value = {
    ...fieldGroupsState.value,
    [category]: remaining,
  }
  markFieldGroupsDirty()

  await clearFieldGroupAssignments(category, group.id)
}

const buildFieldGroupsPayload = () => {
  const payload = {}

  FIELD_GROUP_CATEGORIES.forEach((category) => {
    const groups = getFieldGroups(category).map((group, index) => ({
      id: group.id,
      name: (group.name || '').trim(),
      color: group.color || '#E9ECEF',
      order: index,
    }))

    if (groups.length > 0) {
      payload[category] = groups
    }
  })

  return Object.keys(payload).length > 0 ? payload : null
}

const saveTemplateFieldGroups = async () => {
  if (!selectedTemplateForFields.value || !fieldGroupsDirty.value) {
    return
  }

  const payload = buildFieldGroupsPayload()

  if (payload) {
    const hasEmptyNames = Object.values(payload).some((groups) =>
      groups.some((group) => !group.name || group.name.trim().length === 0),
    )
    if (hasEmptyNames) {
      showError('Bitte vergib Namen für alle Gruppen.')
      return
    }
  }

  savingFieldGroups.value = true
  try {
    const response = await templateService.updateTemplateFieldGroups(
      selectedTemplateForFields.value.id,
      payload,
    )

    const updatedTemplate = response.data || response

    const groups =
      updatedTemplate?.settings?.fieldGroups && Object.keys(updatedTemplate.settings.fieldGroups).length > 0
        ? updatedTemplate.settings.fieldGroups
        : null

    if (groups) {
      selectedTemplateForFields.value.settings = {
        ...(selectedTemplateForFields.value.settings || {}),
        fieldGroups: groups,
      }
    } else if (selectedTemplateForFields.value.settings?.fieldGroups) {
      delete selectedTemplateForFields.value.settings.fieldGroups
    }

    const templateIndex = templates.value.findIndex(
      (tpl) => tpl.id === selectedTemplateForFields.value.id,
    )
    if (templateIndex !== -1) {
      templates.value[templateIndex] = {
        ...templates.value[templateIndex],
        settings: updatedTemplate?.settings || null,
      }
    }

    initializeFieldGroups()
    showSuccess('Template-Gruppen gespeichert')
  } catch (err) {
    console.error('Error saving template groups:', err)
    showError(err.response?.data?.error || 'Fehler beim Speichern der Gruppen')
  } finally {
    savingFieldGroups.value = false
  }
}

const onFieldGroupAssignmentChange = async (field, category, value) => {
  if (!field || !FIELD_GROUP_CATEGORIES.includes(category)) {
    return
  }

  const normalizedGroupId = value && value.length > 0 ? value : null
  const updatedOptions = field.options ? { ...field.options } : {}
  updatedOptions.groupId = normalizedGroupId

  updatingFieldGroupId.value = field.id
  try {
    await templateService.updateTemplateField(field.id, { options: updatedOptions })
    field.options = updatedOptions

    const templateField = selectedTemplateForFields.value?.adminFields?.find(
      (f) => f.id === field.id,
    )
    if (templateField) {
      templateField.options = updatedOptions
    }

    showSuccess('Feld-Gruppe aktualisiert')
  } catch (err) {
    console.error('Error updating field group assignment:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Feld-Gruppe')
  } finally {
    updatingFieldGroupId.value = null
  }
}

const toggleOrderMode = (category) => {
  if (orderMode.value === category) {
    
    orderMode.value = null
    draggedField.value = null
    draggedCategory.value = null
  } else {
    
    orderMode.value = category
  }
}

const dragStart = (event, field, category) => {
  if (orderMode.value !== category) return

  draggedField.value = field
  draggedCategory.value = category
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', field.id)
}

const drop = (event, targetField, category) => {
  if (orderMode.value !== category || !draggedField.value) return

  event.preventDefault()

  const fields = getFieldsByCategory(category)
  const draggedIndex = fields.findIndex((f) => f.id === draggedField.value.id)
  const targetIndex = fields.findIndex((f) => f.id === targetField.id)

  if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return

  
  const reorderedFields = [...fields]
  const [movedField] = reorderedFields.splice(draggedIndex, 1)
  reorderedFields.splice(targetIndex, 0, movedField)

  
  reorderedFields.forEach((field, index) => {
    field.order = index + 1
  })

  
  const templateFields = selectedTemplateForFields.value.adminFields
  reorderedFields.forEach((field) => {
    const templateField = templateFields.find((f) => f.id === field.id)
    if (templateField) {
      templateField.order = field.order
    }
  })

  draggedField.value = null
  draggedCategory.value = null
}

const saveFieldOrder = async (category) => {
  if (!selectedTemplateForFields.value) return

  savingOrder.value = true

  try {
    const fields = getFieldsByCategory(category)
    const orderUpdates = fields.map((field, index) => ({
      id: field.id,
      order: index + 1,
    }))

    
    await Promise.all(
      orderUpdates.map((update) =>
        templateService.updateTemplateField(update.id, { order: update.order }),
      ),
    )

    showSuccess(`Reihenfolge der ${getFieldCategoryLabel(category)}-Felder gespeichert`)
    orderMode.value = null

    
    await loadTemplates()
  } catch (err) {
    console.error('Error saving field order:', err)
    showError('Fehler beim Speichern der Reihenfolge')
  } finally {
    savingOrder.value = false
  }
}


const loadTaxRates = async () => {
  loadingTaxRates.value = true
  try {
    const response = await templateService.getAllTaxRates(true) 
    taxRates.value = response.data || []
  } catch (err) {
    console.error('Error loading tax rates:', err)
    showError('Fehler beim Laden der Steuersätze')
  } finally {
    loadingTaxRates.value = false
  }
}

const createTaxRate = async () => {
  if (!newTaxRate.value.name || !newTaxRate.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  if (newTaxRate.value.rate < 0 || newTaxRate.value.rate > 100) {
    showError('Steuersatz muss zwischen 0 und 100 Prozent liegen')
    return
  }

  creatingTaxRate.value = true

  try {
    await templateService.createTaxRate(newTaxRate.value)
    showSuccess('Steuersatz erfolgreich erstellt')
    showCreateTaxRateModal.value = false
    resetNewTaxRate()
    await loadTaxRates()
  } catch (err) {
    console.error('Error creating tax rate:', err)
    showError(err.response?.data?.error || 'Fehler beim Erstellen des Steuersatzes')
  } finally {
    creatingTaxRate.value = false
  }
}

const editTaxRate = (taxRate) => {
  editingTaxRate.value = { ...taxRate }
  showEditTaxRateModal.value = true
}

const updateTaxRate = async () => {
  if (!editingTaxRate.value.name || !editingTaxRate.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  if (editingTaxRate.value.rate < 0 || editingTaxRate.value.rate > 100) {
    showError('Steuersatz muss zwischen 0 und 100 Prozent liegen')
    return
  }

  updatingTaxRate.value = true

  try {
    await templateService.updateTaxRate(editingTaxRate.value.id, editingTaxRate.value)
    showSuccess('Steuersatz erfolgreich aktualisiert')
    showEditTaxRateModal.value = false
    await loadTaxRates()
  } catch (err) {
    console.error('Error updating tax rate:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Steuersatzes')
  } finally {
    updatingTaxRate.value = false
  }
}

const deleteTaxRate = async (taxRate) => {
  if (taxRate.isDefault) {
    showError('Der Standard-Steuersatz kann nicht gelöscht werden')
    return
  }

  const confirmed = await showConfirmDialog(
    'Steuersatz löschen',
    `Möchtest du den Steuersatz "${taxRate.displayName}" wirklich löschen?`,
  )

  if (!confirmed.isConfirmed) return

  try {
    await templateService.deleteTaxRate(taxRate.id)
    showSuccess('Steuersatz erfolgreich gelöscht')
    await loadTaxRates()
  } catch (err) {
    console.error('Error deleting tax rate:', err)
    showError(err.response?.data?.error || 'Fehler beim Löschen des Steuersatzes')
  }
}

const setDefaultTaxRate = async (taxRate) => {
  if (taxRate.isDefault) {
    showError('Dieser Steuersatz ist bereits der Standard-Steuersatz')
    return
  }

  const confirmed = await showConfirmDialog(
    'Standard-Steuersatz ändern',
    `Möchtest du "${taxRate.displayName}" als neuen Standard-Steuersatz festlegen?`,
  )

  if (!confirmed.isConfirmed) return

  try {
    await templateService.setDefaultTaxRate(taxRate.id)
    showSuccess(`"${taxRate.displayName}" wurde als Standard-Steuersatz festgelegt`)
    await loadTaxRates()
  } catch (err) {
    console.error('Error setting default tax rate:', err)
    showError(err.response?.data?.error || 'Fehler beim Setzen des Standard-Steuersatzes')
  }
}

const resetNewTaxRate = () => {
  newTaxRate.value = {
    name: '',
    displayName: '',
    rate: 19.0,
    description: '',
    isDefault: false,
    isActive: true,
    order: 0,
  }
}

const toggleTemplateActive = async (template) => {
  try {
    await templateService.updateTemplate(template.id, {
      isActive: !template.isActive,
    })

    template.isActive = !template.isActive
    showSuccess(`Template ${template.isActive ? 'aktiviert' : 'deaktiviert'}`)
  } catch (err) {
    console.error('Error toggling template active:', err)
    showError('Fehler beim Ändern des Status')
  }
}

const toggleProFeature = async (template) => {
  if (template.isDefault) {
    showError('Standard-Templates können nicht als Pro-Feature markiert werden')
    return
  }

  try {
    await templateService.updateTemplate(template.id, {
      isProFeature: !template.isProFeature,
    })

    template.isProFeature = !template.isProFeature
    showSuccess(
      `Template als ${template.isProFeature ? 'Pro-Feature markiert' : 'kostenloses Feature markiert'}`,
    )
  } catch (err) {
    console.error('Error toggling pro feature:', err)
    showError('Fehler beim Ändern des Pro-Feature Status')
  }
}

const updateTemplateNeedsSignature = async (template) => {
  updatingSignature.value[template.id] = true
  
  try {
    await templateService.updateTemplate(template.id, {
      needsSignature: template.needsSignature
    })
    
    showSuccess(`Signatur-Einstellung für "${template.displayName}" erfolgreich aktualisiert`)
  } catch (err) {
    console.error('Error updating template needs signature:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren der Signatur-Einstellung')
    
    // Revert the change on error
    template.needsSignature = !template.needsSignature
  } finally {
    updatingSignature.value[template.id] = false
  }
}

const getCategoryLabel = (template) => {
  if (template.category) {
    return template.category.displayName
  }
  return 'Keine Kategorie'
}

const getCategoryBadgeClass = (template) => {
  return 'bg-primary'
}

const resetNewTemplate = () => {
  newTemplate.value = {
    name: '',
    displayName: '',
    description: '',
    categoryId: '',
    isProFeature: false,
    allowCustomFields: true,
    personalCategoryName: '',
    personalCategoryIcon: '',
    receiptCategoryName: '',
    receiptCategoryIcon: '',
    financialCategoryName: '',
    financialCategoryIcon: '',
  }
}

const createCategory = async () => {
  if (!newCategory.value.name || !newCategory.value.displayName) {
    showError('Name und Anzeigename sind erforderlich')
    return
  }

  try {
    await templateService.createCategory(newCategory.value)
    showSuccess('Kategorie erfolgreich erstellt')
    resetNewCategory()
    await loadCategories()
  } catch (err) {
    console.error('Error creating category:', err)
    showError(err.response?.data?.message || 'Fehler beim Erstellen der Kategorie')
  }
}

const deleteCategory = async (category) => {
  const confirmed = await showConfirmDialog(
    'Kategorie löschen',
    `Möchtest du die Kategorie "${category.displayName}" wirklich löschen?`,
  )

  if (!confirmed.isConfirmed) return

  try {
    await templateService.deleteCategory(category.id)
    showSuccess('Kategorie erfolgreich gelöscht')
    await loadCategories()
    await loadTemplates()
  } catch (err) {
    console.error('Error deleting category:', err)
    showError(err.response?.data?.message || 'Fehler beim Löschen der Kategorie')
  }
}

const resetNewCategory = () => {
  newCategory.value = {
    name: '',
    displayName: '',
    description: '',
    color: '#6c757d',
    icon: 'folder',
  }
  showNewCategoryForm.value = false
}


const updateSelectOptions = () => {
  if (selectOptionsText.value) {
    const options = selectOptionsText.value
      .split('\n')
      .map((option) => option.trim())
      .filter((option) => option.length > 0)
    newField.value.options.options = options
  } else {
    newField.value.options.options = []
  }
}

const updateEditSelectOptions = () => {
  if (editSelectOptionsText.value) {
    const options = editSelectOptionsText.value
      .split('\n')
      .map((option) => option.trim())
      .filter((option) => option.length > 0)
    editingField.value.options.options = options
  } else {
    editingField.value.options.options = []
  }
}

const initializeEditSelectOptions = (field) => {
  if (field.type === 'select' && field.options?.options) {
    editSelectOptionsText.value = field.options.options.join('\n')
  } else {
    editSelectOptionsText.value = ''
  }
}


const clearSelectOptions = () => {
  selectOptionsText.value = ''
  newField.value.options.options = []
}

watch(showFieldsModal, (isOpen) => {
  if (isOpen) {
    initializeFieldGroups()
  } else {
    fieldGroupsDirty.value = false
  }
})

watch(
  () => selectedTemplateForFields.value?.id,
  () => {
    if (showFieldsModal.value) {
      initializeFieldGroups()
    }
  },
)

// Watch for category changes and reset type if not allowed
watch(() => newField.value.category, (newCategory) => {
  if (newField.value.type === 'repeater' && newCategory !== 'receipt') {
    newField.value.type = 'text'
  }
  if (newField.value.type === 'money' && newCategory !== 'financial') {
    newField.value.type = 'text'
  }
  const groups = getFieldGroups(newCategory)
  if (!groups.some((group) => group.id === newField.value.options.groupId)) {
    newField.value.options.groupId = null
  }
})

watch(() => editingField.value.category, (newCategory) => {
  if (editingField.value.type === 'repeater' && newCategory !== 'receipt') {
    editingField.value.type = 'text'
  }
  if (editingField.value.type === 'money' && newCategory !== 'financial') {
    editingField.value.type = 'text'
  }
  const groups = getFieldGroups(newCategory)
  if (!groups.some((group) => group.id === editingField.value.options.groupId)) {
    editingField.value.options.groupId = null
  }
})

onMounted(async () => {
  await Promise.all([loadTemplates(), loadCategories()])
})


const openTaxRatesModal = async () => {
  showTaxRatesModal.value = true
  await loadTaxRates()
}


const openCreateFieldModal = async () => {
  clearSelectOptions()
  
  // Reset newField to default values
  newField.value = {
    name: '',
    displayName: '',
    type: 'text',
    category: 'receipt',
    required: false,
    placeholder: '',
    description: '',
    defaultValue: '',
    isCalculated: false,
    calculationFormula: '',
    validation: {
      min: null,
      max: null
    },
    options: {
      includeTaxRate: false,
      availableTaxRates: [],
      unit: '',
      pricePerUnit: 0, 
      options: [],
      groupId: null,
      // Repeater options
      repeaterMinItems: 0,
      repeaterMaxItems: null,
      repeaterIncludeTaxRate: false,
      repeaterTaxRateSelectionMode: 'user_choice',
      repeaterAvailableTaxRates: [],
      repeaterSelectedTaxRateId: '',
    },
  }
  
  showCreateFieldModal.value = true
  await loadTaxRates()
}
</script>

<style scoped>
.border-dashed {
  border: 2px dashed #dee2e6 !important;
  transition: all 0.3s ease;
}

.border-dashed:hover {
  border-color: var(--bs-primary) !important;
  background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.field-group-item {
  background: #fff;
  transition: background-color 0.2s ease;
}

.field-group-item:hover {
  background-color: #f8f9fa;
}

.group-color-dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: inline-block;
}
</style>
